/**
 * Dealer Desk pop-ups, laid out like the old back office (Everlead): edit an open position, edit a
 * closed one, and open a position for the client at the live price. They follow the desk's rules:
 *
 * - SL / TP and closing change the trade itself.
 * - Open / close price, commission and swap never edit the trade: Save posts a correction (or a
 *   rollover adjustment) of the difference, through approval when the role needs it. "Change in
 *   profit" is that difference, previewed live from the server.
 * - Delete on a closed position is the "cancel trade" correction (its P&L reversed).
 * - A transferred trade (opened with "Transferred trade" ticked) takes its previous broker's dates,
 *   prices, swap and commission once, saved directly; the balance moves by the trade's real result
 *   (TRADE_TRANSFER_PLAN.md).
 * - "From chart" (CHART_ENTRY_PLAN.md): Open position takes a past price and minute clicked on the
 *   chart as the trade's open; Edit position's "Pick on chart" fills the open (or close) fields the same way,
 *   and Close position can close at one ("At a past price"). Edit and Close show the Open pop-up's checks:
 *   our candle at that minute, a TP / SL reached in between, the swap since (desk/chart-entry.ts previewPoint).
 * - A master account's trade takes the same edits, corrections (swap, commission, delete) and restore as a
 *   client's; they change the master's trade only, on its own balance, and the pop-ups list the
 *   followers' copies they leave as they are (MASTER_EDITS_PLAN.md).
 * - Edit order (the Pending tab): a pending order's type, price, amount, leverage, TP and SL, a client's
 *   or a master's; limit or stop follows from the type and the price (desk/dealing.ts dealerEditOrder).
 * - Each pop-up closes once its action goes through (placed, saved, closed, cancelled, restored or sent for
 *   approval) and the page reloads; a note in the corner says what was done (DEALER_POPUP_CLOSE_PLAN.md).
 *   A refusal or an error keeps it open, with the form as it was typed.
 * - A master opens and closes from chart too: its followers' copies open (and close) at the same price and
 *   minute, and the copy list shows each one's profit or loss since the entry.
 * - Close… on an open position's row: a small pop-up that closes it at the live price (25%, 50% or all)
 *   without opening Edit; a close at a past price is in Edit.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  checkDeskOpen,
  closePrice as exitPrice,
  D,
  fmt,
  fmtDateTime,
  fmtUsd,
  initialMargin,
  isMarketOpen,
  lotsToQty,
  nextMarketOpen,
  notionalOf,
  openFeeFor,
  pendingKind,
  pnl,
  usdPerPoint,
  validateTpSl,
  type Candle,
  type DeskFix,
  type DeskFixLink,
  type DeskOpenFacts,
  type DeskOpenForm,
  type DeskRefusal,
  type MasterCopyPreview,
} from '@platform/shared';
import { InstrumentIcon, InstrumentLabel } from '../components/InstrumentIcon';
import { PairName } from '../components/PairName';
import { cx, fmtTradeId, Modal, sideLabel, Spinner, TradeId } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useLoad } from '../stores/account';
import { useInstrument, useInstruments, useQuote, useTicker } from '../stores/market';
import { deskNotice, MONEY_BUTTON, REASONS, useDesk, type DeskCtx, type DeskSummary } from './AdminDeskDealing';
import {
  ClosedResult,
  closedPerPoint,
  DeskChart,
  InstrumentPicker,
  LeverageField,
  leverageProblem,
  londonLabel,
  OpenEstimate,
  OpenPositionNow,
  OpenRateImpact,
  PlainWords,
  utcLabel,
  type DeskLine,
  type DeskMark,
  type LeverageRange,
} from './AdminDeskTradeTools';

export interface Pos {
  id: string;
  /** Trade number ('#100245'), shared with the order that opened it. */
  tradeId: number | null;
  symbol: string;
  side: 'long' | 'short';
  qty: string;
  entryPrice: string;
  leverage: number;
  margin?: string;
  marginHeld?: string;
  swap: string;
  openFee: string;
  closeFee: string | null;
  tpPrice: string | null;
  slPrice: string | null;
  openedAt: string;
  closedAt: string | null;
  closePrice: string | null;
  realizedPnl: string | null;
  closeReason: string | null;
  currentPrice?: string | null;
  unrealizedPnl?: string | null;
  liqPrice?: string | null;
  openedBy?: string | null;
  closedBy?: string | null;
  source?: string;
  transfer?: { broker: string | null; ticket: string | null; transferredAt: string | null } | null;
}

const input = 'h-9 w-full rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted disabled:opacity-60';
const btn = 'h-9 rounded-lg px-4 text-xs font-semibold disabled:opacity-40';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');
/** Buy green, sell red. */
// `!`: over the box's own border, background and text colour (cx doesn't merge classes).
const sideTone = (s: string) => (s === 'long' ? 'border-up/50! bg-up/10! font-semibold text-up!' : 'border-down/50! bg-down/10! font-semibold text-down!');
const tone = (x: string | null | undefined) => (x == null || D.from(x).isZero() ? '' : D.from(x).isNeg() ? 'text-down' : 'text-up');
const PRICE = /^\d{1,12}(\.\d{1,12})?$/;
const SIGNED = /^-?\d{1,12}(\.\d{1,2})?$/;
const signed = (x: string | null | undefined) => (x == null ? '—' : `${D.from(x).isNeg() ? '−' : D.from(x).isPos() ? '+' : ''}${fmtUsd(D.from(x).abs())}`);
const same = (a: string, b: string | null | undefined) => b != null && PRICE.test(a.replace('-', '')) && D.from(a).eq(D.from(b));

function Row({ label, children, wide }: { label: ReactNode; children: ReactNode; wide?: boolean }) {
  return (
    <div className={cx('grid grid-cols-[112px_1fr] items-start gap-2 text-[12px] text-muted', wide && 'sm:col-span-2')}>
      <span className="flex h-9 items-center">{label}</span>
      {children}
    </div>
  );
}
const Shown = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cx(input, 'num flex items-center bg-raised/40', className)}>{children}</div>
);
const Section = ({ title }: { title: string }) => <div className="-mx-5 border-y border-line bg-raised/50 px-5 py-1.5 text-xs font-semibold text-accent">{title}</div>;

/** The account's figures, always open: ID, equity, margin level, balance, open P&L, credit, margin used, free margin (and what the trade on screen leaves of it), open lots. */
function AccountStrip({ s, after }: { s: DeskSummary | null; after?: { margin: string | null; label: string } }) {
  if (!s) return null;
  const pct = s.marginLevel ? `${D.from(s.marginLevel).mul(100).toFixed(2)}%` : '—';
  const freeAfter = after?.margin && s.marginUsed !== undefined ? D.from(s.freeMargin).sub(after.margin) : null;
  return (
    <div className="-mx-5 -mt-1 grid gap-x-5 gap-y-1.5 border-y border-line bg-raised/50 px-5 py-2.5 sm:grid-cols-3">
      <Row label="Account ID">
        <Shown>{s.label}</Shown>
      </Row>
      <Row label="Equity">
        <Shown>{fmtUsd(s.equity)}</Shown>
      </Row>
      <Row label="Margin level">
        <Shown>{pct}</Shown>
      </Row>
      <Row label="Balance">
        <Shown className={tone(s.balance)}>{fmtUsd(s.balance)}</Shown>
      </Row>
      <Row label="Open P&L">
        <Shown className={tone(s.openPnl)}>{signed(s.openPnl)}</Shown>
      </Row>
      <Row label="Credit">
        <Shown>{fmtUsd(s.credit)}</Shown>
      </Row>
      <Row label="Margin used">
        <Shown>{s.marginUsed !== undefined ? fmtUsd(s.marginUsed) : '—'}</Shown>
      </Row>
      <Row label="Free margin">
        <Shown>
          {fmtUsd(s.freeMargin)}
          {freeAfter && (
            <span className={cx('ml-auto whitespace-nowrap pl-2 text-[11px]', freeAfter.isNeg() ? 'text-down' : 'text-muted')} title={after!.label}>
              → {fmtUsd(freeAfter)}
            </span>
          )}
        </Shown>
      </Row>
      <Row label="Open lots">
        <Shown>{s.openLots !== undefined ? `${s.openLots} lots` : '—'}</Shown>
      </Row>
    </div>
  );
}

/** A box with a $ (or 1:) in front of what the dealer types, and a note under it: the other unit, the margin it needs. */
function Affixed({ prefix, suffix, children }: { prefix?: string; suffix?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <div className="relative flex min-w-0 items-center">
        {prefix && <span className="pointer-events-none absolute left-3 text-[13px] text-muted">{prefix}</span>}
        <div className={cx('min-w-0 flex-1', prefix && '[&>input]:pl-7 [&>div]:pl-7')}>{children}</div>
      </div>
      {suffix && <span className="num text-[11px] leading-tight text-muted">{suffix}</span>}
    </div>
  );
}
/** Units with thousands separators: 50,000 EUR. */
const fmtQty = (q: string | D) => Number(q.toString()).toLocaleString('en-US', { maximumFractionDigits: 8 });

/** A number box with an on / off tick, like Everlead's Stop Loss / Take Profit. */
function Toggled({ label, on, value, disabled, bad, onOn, onValue }: { label: string; on: boolean; value: string; disabled?: boolean; /** Outlined: the refusal note names it. */ bad?: boolean; onOn: (v: boolean) => void; onValue: (v: string) => void }) {
  return (
    <Row
      label={
        <label className="flex items-center gap-1.5">
          <input type="checkbox" checked={on} disabled={disabled} onChange={(e) => onOn(e.target.checked)} />
          {label}
        </label>
      }
    >
      <input className={cx(input, 'num', bad && 'border-warn!')} value={value} disabled={disabled || !on} onChange={(e) => onValue(e.target.value)} aria-label={label} />
    </Row>
  );
}

type Correction = { label: string; body: Record<string, string> };

/** Edit open position / Edit closed position. */
export function PositionDialog({ pos, closed, onClose, onRestore }: { pos: Pos; closed: boolean; onClose: () => void; /** A closed trade the person may restore: opens Restore trade. */ onRestore?: () => void }) {
  const ctx = useDesk();
  const quote = useQuote(closed ? '' : pos.symbol);
  const inst = useInstrument(pos.symbol);
  const [candles, setCandles] = useState<Candle[] | null>(null);
  const range = useMemo(() => ({ from: Date.parse(pos.openedAt), to: Date.parse(pos.closedAt ?? pos.openedAt) }), [pos.openedAt, pos.closedAt]);
  const fees = D.from(pos.openFee).add(pos.closeFee ?? 0).toFixed(2);
  const [f, setF] = useState({
    open: pos.entryPrice,
    close: pos.closePrice ?? '',
    side: pos.side,
    /** Typed in lots; '' until the instrument (and its lot size) is known. */
    qty: '',
    leverage: String(pos.leverage),
    openedAt: toBox(pos.openedAt),
    closedAt: toBox(pos.closedAt),
    commission: fees,
    swap: D.from(pos.swap).toFixed(2),
    slOn: !!pos.slPrice,
    sl: pos.slPrice ?? '',
    tpOn: !!pos.tpPrice,
    tp: pos.tpPrice ?? '',
    /** Close from chart: the past minute and price, and the picked candle's length (minutes). */
    exitAt: '',
    exitPrice: '',
    exitSpan: 1,
    /** The picked candle's length for the open and close fields (a longer candle: the minute that traded). */
    openSpan: 1,
    closeSpan: 1,
    reasonCode: 'wrong_rate',
    evidence: '',
    clientNote: '',
  });
  const [change, setChange] = useState<{ amount: string | null; error: string | null; preview: EditPreview | null }>({ amount: '0', error: null, preview: null });
  const lotsNow = inst ? D.from(pos.qty).div(inst.lotSize) : null;
  useEffect(() => {
    if (lotsNow && f.qty === '') setF((x) => ({ ...x, qty: lotsNow.toString() }));
  }, [lotsNow?.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  /** The typed amount in the instrument's units (what the API takes). */
  const qtyUnits = inst && PRICE.test(f.qty) && D.from(f.qty).isPos() ? D.from(f.qty).mul(inst.lotSize) : null;
  const [busy, setBusy] = useState(false);
  /** Close position…: at the live price, or at a past price and minute from the chart. */
  const [closing, setClosing] = useState<false | 'live' | 'chart'>(false);
  const [deleting, setDeleting] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  /** "Pick on chart": which of the trade's moments a click on the chart sets (exit: a close from chart). */
  const [picking, setPicking] = useState<'open' | 'close' | 'exit' | null>(null);
  const set = (k: keyof typeof f, v: string | boolean | number) => setF((x) => ({ ...x, [k]: v }));
  const copies = useCopies(ctx?.masterId ?? null, pos.id);

  const canPrice = !!ctx?.canCorrect && closed;
  // Type, amount, leverage, prices and times change the trade itself (desk/trade-edits.ts), a master's too.
  const canEdit = !!ctx?.canEdit;
  const canSwap = !!ctx?.canRollover;
  const canTpSl = !!ctx?.canTrade && !closed;
  // Leverage keeps to what a new order may use: the instrument's range, under the client's or the master's cap.
  const levRange: LeverageRange | null = inst
    ? { min: inst.minLeverage, max: inst.maxLeverage, cap: ctx?.leverageCap ?? null, capOf: ctx?.master ? `${ctx.summary?.label ?? 'this master'}’s` : 'this client’s', now: pos.leverage }
    : null;

  // What Save would do: SL / TP straight on the trade, everything else as corrections.
  const sl = f.slOn && f.sl.trim() ? f.sl.trim() : null;
  const tp = f.tpOn && f.tp.trim() ? f.tp.trim() : null;
  // Turned round: an open trade's TP and SL go with the edit and are checked against the new type there, not on their own.
  const sideChanged = canEdit && f.side !== pos.side;
  const turned = sideChanged && !closed;
  const tpslChanged = canTpSl && !turned && (sl !== (pos.slPrice ?? null) || tp !== (pos.tpPrice ?? null));
  const tpslValid = (!sl || PRICE.test(sl)) && (!tp || PRICE.test(tp));
  // A TP or SL already passed on the new type would close the trade at once.
  const turnRef = turned && quote ? exitPrice(f.side, { bid: D.from(quote.bid), ask: D.from(quote.ask) }) : null;
  const tpslWrong = turnRef && tpslValid ? validateTpSl(f.side, turnRef, tp ? D.from(tp) : null, sl ? D.from(sl) : null) : null;
  const corrections: Correction[] = [];
  let invalid = ((tpslChanged || turned) && !tpslValid) || !!tpslWrong;
  const edit: Record<string, string | number | null> = {};
  if (canEdit) {
    if (sideChanged) Object.assign(edit, closed ? { side: f.side } : { side: f.side, tpPrice: tp, slPrice: sl });
    if (f.qty !== '' && !(qtyUnits && qtyUnits.eq(pos.qty))) qtyUnits ? (edit.qty = qtyUnits.toString()) : (invalid = true);
    if (f.leverage.trim() !== String(pos.leverage)) !(levRange ? leverageProblem(f.leverage, levRange) : !/^\d{1,4}$/.test(f.leverage.trim())) ? (edit.leverage = Number(f.leverage)) : (invalid = true);
    if (closed && !same(f.close, pos.closePrice)) PRICE.test(f.close) ? (edit.closePrice = f.close) : (invalid = true);
    if (closed && f.closedAt !== toBox(pos.closedAt)) fromBox(f.closedAt) ? (edit.closedAt = fromBox(f.closedAt)) : (invalid = true);
    if (!same(f.open, pos.entryPrice)) PRICE.test(f.open) ? (edit.openPrice = f.open) : (invalid = true);
    if (f.openedAt !== toBox(pos.openedAt)) fromBox(f.openedAt) ? (edit.openedAt = fromBox(f.openedAt)) : (invalid = true);
  }
  const editing = Object.keys(edit).length > 0;
  if (canPrice) {
    if (!canEdit && !same(f.open, pos.entryPrice)) PRICE.test(f.open) ? corrections.push({ label: 'Open price', body: { kind: 'price', field: 'open', price: f.open } }) : (invalid = true);
    if (!canEdit && !same(f.close, pos.closePrice)) PRICE.test(f.close) ? corrections.push({ label: 'Close price', body: { kind: 'price', field: 'close', price: f.close } }) : (invalid = true);
    if (!same(f.commission, fees)) PRICE.test(f.commission) ? corrections.push({ label: 'Commission', body: { kind: 'commission', commission: f.commission } }) : (invalid = true);
  }
  if (canSwap && f.swap.trim() !== D.from(pos.swap).toFixed(2)) {
    if (SIGNED.test(f.swap.trim()) && !D.from(f.swap.trim()).eq(D.from(pos.swap).round(2))) {
      corrections.push({ label: 'Swap', body: { kind: 'rollover', amount: D.from(f.swap.trim()).sub(D.from(pos.swap).round(2)).toFixed(2) } });
    } else if (!SIGNED.test(f.swap.trim())) invalid = true;
  }
  const key = JSON.stringify([corrections.map((c) => c.body), edit]);

  // Change in profit: the sum of what the edit and the corrections would post, from the server's own preview.
  useEffect(() => {
    if (!corrections.length && !editing) return setChange({ amount: '0', error: null, preview: null });
    setChange((c) => ({ ...c, amount: null, error: null }));
    const t = window.setTimeout(() => {
      Promise.all([
        ...(editing ? [api.post<EditPreview>(`/api/admin/desk/positions/${pos.id}/edit/preview`, edit)] : []),
        ...corrections.map((c) => api.post<{ amount: string }>(`/api/admin/desk/positions/${pos.id}/corrections/preview`, c.body)),
      ])
        .then((rs) => setChange({ amount: rs.reduce((s, r) => s.add(r.amount), D.ZERO).toFixed(2), error: null, preview: editing ? (rs[0] as EditPreview) : null }))
        .catch((e) => setChange({ amount: null, error: err(e), preview: null }));
    }, 400);
    return () => window.clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ctx) return null;
  // Edits save straight away; only deleting a trade asks for the reason and evidence.
  const details = deleting;
  // A master's trade is paper money with no client statement: the comment is enough.
  const detailsOk = !!f.evidence.trim() && (ctx.master || !!f.clientNote.trim());
  const canSave = !busy && !invalid && (tpslChanged || editing || corrections.length > 0) && !change.error;
  const why = (c: Correction) => ({
    ...c.body,
    reasonCode: f.reasonCode,
    evidence: f.evidence.trim() || 'Edited on the Dealer Desk',
    clientNote: f.clientNote.trim() || 'Trade details corrected',
  });
  const outcome = (label: string, r: Record<string, any>) => (r.pendingApproval ? `${label}: sent for approval as APR-${r.pendingApproval.number}` : `${label}: posted ${fmtUsd(r.amount)}`);

  // Each step says what it did as it goes through. Once they all have, the pop-up closes, the page
  // reloads and the corner note says what was done (DEALER_POPUP_CLOSE_PLAN.md). When the first step
  // fails nothing was done: the pop-up stays open with the error, to fix and try again. When a later
  // step of a Save fails it closes all the same, since saving the form again would repeat the steps
  // already done (a swap adjustment is a difference: it would post twice); the note says what wasn't.
  const run = async (steps: (did: string[]) => Promise<void>) => {
    setBusy(true);
    setFail(null);
    const did: string[] = [];
    let failed: string | null = null;
    try {
      await steps(did);
    } catch (e) {
      failed = err(e);
    }
    setBusy(false);
    if (failed && !did.length) return setFail(failed);
    ctx.refresh();
    deskNotice({ title: `${pos.tradeId != null ? `Order ${fmtTradeId(pos.tradeId)}` : `Position ${pos.id.slice(0, 8)}`} · ${what}`, lines: did, fail: failed && `Not saved: ${failed}` });
    onClose();
  };
  const save = () =>
    run(async (did) => {
      if (editing) {
        const r = await api.post<Record<string, any>>(`/api/admin/desk/positions/${pos.id}/edit`, { ...edit, note: f.clientNote.trim() });
        did.push(r.pendingApproval ? `Trade edit: sent for approval as APR-${r.pendingApproval.number}` : D.from(r.amount).isZero() ? 'Trade details saved' : `Trade details saved: ${fmtUsd(r.amount)} on the balance`);
      }
      if (tpslChanged) {
        await api.post(`/api/admin/desk/positions/${pos.id}/tpsl`, { tpPrice: tp, slPrice: sl });
        did.push('Stop loss / take profit saved');
      }
      for (const c of corrections) did.push(outcome(c.label, await api.post<Record<string, any>>(`/api/admin/desk/positions/${pos.id}/corrections`, why(c))));
    });
  const closeAt = (fraction: string) =>
    run(async (did) => {
      await api.post(`/api/admin/desk/positions/${pos.id}/close`, { fraction });
      did.push(fraction === '1' ? 'Position closed at the live price' : `${Number(fraction) * 100}% closed at the live price`);
    });
  // Close from chart: at the picked past price and minute (desk/chart-entry.ts).
  const exitIso = fromBox(f.exitAt);
  const exitMs = exitIso ? Date.parse(exitIso) : null;
  const exitPx = PRICE.test(f.exitPrice) ? f.exitPrice : null;
  const exitReady = !!exitIso && !!exitPx && exitMs! > Date.parse(pos.openedAt) && exitMs! <= Date.now();
  const closeFromChart = (fraction: string) =>
    run(async (did) => {
      const r = await api.post<{ closePrice: string; closedAt: string }>(`/api/admin/desk/positions/${pos.id}/close`, { fraction, exit: { price: exitPx, at: exitIso } });
      did.push(`${fraction === '1' ? 'Position' : `${Number(fraction) * 100}% of the position`} closed at ${r.closePrice}, ${utcLabel(Date.parse(r.closedAt))}`);
    });
  const cancelTrade = () =>
    run(async (did) => {
      did.push(outcome('Trade cancelled', await api.post<Record<string, any>>(`/api/admin/desk/positions/${pos.id}/corrections`, why({ label: '', body: { kind: 'cancel' } }))));
    });

  // An open trade's view (Position now, In plain words) follows the edited type, amount and open price as they are typed.
  const shown = { ...pos, side: f.side, qty: qtyUnits ? qtyUnits.toString() : pos.qty, entryPrice: PRICE.test(f.open) ? f.open : pos.entryPrice };
  const lev = /^\d{1,4}$/.test(f.leverage.trim()) && Number(f.leverage) >= 1 ? Number(f.leverage) : pos.leverage;
  // A closed trade's Result follows the typed figures too, valued per unit the way it was booked; its margin
  // is worked out again once the amount, leverage or open price changes.
  const booked = { side: pos.side, qty: pos.qty, entryPrice: pos.entryPrice, closePrice: pos.closePrice };
  const remargin = !D.from(shown.qty).eq(pos.qty) || lev !== pos.leverage || !D.from(shown.entryPrice).eq(pos.entryPrice);
  const typedClosed = { ...shown, closePrice: PRICE.test(f.close) ? f.close : pos.closePrice, leverage: lev, margin: remargin ? undefined : pos.margin };
  // What the trade on screen holds: the typed amount, open price and leverage (a hedge may hold less; the preview says).
  const marginNow = pos.marginHeld ?? pos.margin ?? null;
  const marginTyped = inst ? initialMargin(notionalOf(shown.qty, shown.entryPrice, usdPerPoint(inst.contractSize, quote?.fx)), lev) : null;
  const liveRate = closed ? null : quote ? (shown.side === 'long' ? quote.bid : quote.ask) : (pos.currentPrice ?? null);
  // Current profit is the trade as it stands, at its own close rate; Change in profit what the edit does to it
  // (MASTER_EDITS_PLAN.md decision 3). The row's figure is from when the page loaded, so the live price is used.
  const bookedRate = closed ? null : quote ? (pos.side === 'long' ? quote.bid : quote.ask) : (pos.currentPrice ?? null);
  const pvNow = inst ? usdPerPoint(inst.contractSize, quote?.fx) : null;
  const profit = closed ? pos.realizedPnl : inst && quote && bookedRate ? pnl(pos.side, pos.entryPrice, bookedRate, pos.qty, pvNow!).toFixed(2) : (pos.unrealizedPnl ?? null);
  const typedProfit = !closed && inst && quote && liveRate ? pnl(shown.side, shown.entryPrice, liveRate, shown.qty, pvNow!).round(2) : null;
  const profitMove = typedProfit && profit != null ? typedProfit.sub(profit) : D.ZERO;
  const posted = change.amount == null ? null : D.from(change.amount);
  const totalChange = closed ? posted : posted?.add(profitMove) ?? null;
  const changeNote = closed
    ? posted && !posted.isZero()
      ? 'Posted to the balance on Save'
      : null
    : !profitMove.isZero() && posted && !posted.isZero()
      ? `${signed(posted.toString())} posted now (swap); the rest settles when the trade closes`
      : !profitMove.isZero()
        ? 'Settles when the trade closes · nothing posted now'
        : posted && !posted.isZero()
          ? 'Posted to the balance on Save'
          : null;
  // Free margin after the edit, from the server's preview (an open trade's new margin).
  const shortMargin = !closed && change.preview?.margin && D.from(change.preview.margin.freeAfter).isNeg() ? `Not enough free margin: with these figures it would be ${fmtUsd(change.preview.margin.freeAfter)}.` : null;
  // Close from chart: the result at the picked price.
  const exitResult = exitPx && inst ? pnl(pos.side, pos.entryPrice, exitPx, pos.qty, pvNow!) : null;
  const ref = pos.tradeId != null ? `order ${fmtTradeId(pos.tradeId)}` : pos.id.slice(0, 8);
  const what = `${sideLabel(pos.side)} ${lotsNow ? `${lotsNow.toString()} lots ` : ''}${pos.symbol}`;
  const title = `${closed ? 'Edit closed position' : 'Edit open position'}: ${ref} · ${what}${marginNow ? ` · margin ${fmtUsd(marginNow)}` : ''}`;
  const heading = (
    <>
      {closed ? 'Edit closed position' : 'Edit open position'}: {ref} · <span className={pos.side === 'long' ? 'text-up' : 'text-down'}>{sideLabel(pos.side)}</span> {lotsNow ? `${lotsNow.toString()} lots ` : ''}
      <InstrumentLabel symbol={pos.symbol} size={20} />
      {marginNow ? ` · margin ${fmtUsd(marginNow)}` : ''}
    </>
  );
  // The chart's lines follow the form: a typed price or a dragged TP / SL shows at once. While a moment is
  // being picked, its line drags too, to fine-tune the price.
  const chartLines: DeskLine[] = closed
    ? [
        { id: 'open', price: PRICE.test(f.open) ? f.open : pos.entryPrice, label: 'Open', tone: 'accent', drag: picking === 'open' },
        { id: 'close', price: PRICE.test(f.close) ? f.close : pos.closePrice, label: 'Close', tone: 'up', dashed: true, drag: picking === 'close' },
      ]
    : [
        { id: 'entry', price: shown.entryPrice, label: 'Entry', tone: 'accent', drag: picking === 'open' },
        { id: 'tp', price: tp, label: 'TP', tone: 'up', dashed: true, drag: canTpSl },
        { id: 'sl', price: sl, label: 'SL', tone: 'down', dashed: true, drag: canTpSl },
        { id: 'stop', price: pos.liqPrice ?? null, label: 'Stop-out', tone: 'warn', dashed: true },
        ...(closing === 'chart' ? [{ id: 'exit', price: exitPx, label: 'Close', tone: 'down' as const, drag: true }] : []),
      ];
  const dragged = (id: string, price: string) => {
    if (id === 'tp') setF((x) => ({ ...x, tpOn: true, tp: price }));
    if (id === 'sl') setF((x) => ({ ...x, slOn: true, sl: price }));
    if (id === 'entry' || id === 'open') setF((x) => ({ ...x, open: price }));
    if (id === 'close') setF((x) => ({ ...x, close: price }));
    if (id === 'exit') setF((x) => ({ ...x, exitPrice: price }));
  };
  // The marks follow the typed (or picked) times.
  const openedMs = Date.parse(fromBox(f.openedAt) || pos.openedAt);
  const closedMs = closed && pos.closedAt ? Date.parse(fromBox(f.closedAt) || pos.closedAt) : null;
  const pickAt = picking === 'open' ? openedMs : picking === 'close' ? closedMs : picking === 'exit' ? exitMs : null;
  const picked = (t: number, price: string, span: number) => {
    const box = toBox(new Date(t).toISOString());
    if (picking === 'open') setF((x) => ({ ...x, open: price, openedAt: box, openSpan: span }));
    if (picking === 'close') setF((x) => ({ ...x, close: price, closedAt: box, closeSpan: span }));
    if (picking === 'exit') setF((x) => ({ ...x, exitPrice: price, exitAt: box, exitSpan: span }));
  };
  // The checks under a moment that changed (or was picked): our candle, a TP / SL reached in between, the swap since.
  const openMoved = !same(f.open, pos.entryPrice) || f.openedAt !== toBox(pos.openedAt);
  const closeMoved = closed && (!same(f.close, pos.closePrice) || f.closedAt !== toBox(pos.closedAt));
  return (
    <Modal open onClose={onClose} title={title} heading={heading} width={inst ? 1040 : 760}>
      <AccountStrip s={ctx.summary} after={!closed && marginTyped && marginNow && !marginTyped.eq(marginNow) ? { margin: marginTyped.sub(marginNow).toString(), label: 'after this edit' } : undefined} />
      <Section title="Position" />
      <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
        <Row label="Order ID">
          <Shown>
            <TradeId id={pos.tradeId} className="text-[13px]" />
          </Shown>
        </Row>
        <Row label={closed ? 'Closed position ID' : 'Position ID'}>
          <Shown>{pos.id.slice(0, 8)}</Shown>
        </Row>
        <Row label="Type">
          {canEdit ? (
            <select className={cx(input, sideTone(f.side))} value={f.side} onChange={(e) => set('side', e.target.value)} aria-label="Type">
              <option value="long" className="text-up">
                {sideLabel('long')}
              </option>
              <option value="short" className="text-down">
                {sideLabel('short')}
              </option>
            </select>
          ) : (
            <Shown className={sideTone(pos.side)}>{sideLabel(pos.side)}</Shown>
          )}
        </Row>
        <Row label="Instrument">
          <Shown className="gap-2">
            <InstrumentIcon symbol={pos.symbol} size={20} />
            <PairName symbol={pos.symbol} assetClass={inst?.assetClass} tag={false} className="font-semibold" />
            {inst && <span className="min-w-0 truncate text-[11px] text-muted">{inst.name}</span>}
          </Shown>
        </Row>
        <Row label="Amount (lots)">
          <Affixed suffix={qtyUnits && inst ? `= ${fmtQty(qtyUnits)} ${inst.unit}${!closed && marginTyped ? ` · ${fmtUsd(notionalOf(shown.qty, shown.entryPrice, usdPerPoint(inst.contractSize, quote?.fx)), 0)} position` : ''}` : lotsNow && inst ? `= ${fmtQty(pos.qty)} ${inst.unit}` : undefined}>
            {canEdit ? <input className={cx(input, 'num')} value={f.qty} onChange={(e) => set('qty', e.target.value)} aria-label="Amount" /> : <Shown>{lotsNow ? lotsNow.toString() : pos.qty}</Shown>}
          </Affixed>
        </Row>
        <Row label="Leverage">
          {canEdit && levRange ? (
            <LeverageField value={f.leverage} onChange={(v) => set('leverage', v)} range={levRange} defaultLev={inst?.defaultLeverage} margin={closed ? undefined : { before: marginNow ? D.from(marginNow) : null, after: marginTyped }} problem={shortMargin} />
          ) : (
            <Affixed prefix="1:" suffix={marginNow ? `margin ${fmtUsd(marginNow)}` : undefined}>
              <Shown>{pos.leverage}</Shown>
            </Affixed>
          )}
        </Row>
        <Row label="Open price">
          <input className={cx(input, 'num')} value={f.open} disabled={!canEdit && !canPrice} onChange={(e) => set('open', e.target.value)} aria-label="Open price" title={canEdit || closed ? undefined : 'Close the position first to correct its open price'} />
        </Row>
        <Row label="Open time (UTC)">
          {canEdit ? (
            <TimeBox value={f.openedAt} onChange={(v) => setF((x) => ({ ...x, openedAt: v, openSpan: 1 }))} label="Open time (UTC)" />
          ) : (
            <Shown>{fmtDateTime(pos.openedAt)}</Shown>
          )}
        </Row>
        {closed ? (
          <>
            <Row label="Close price">
              <input className={cx(input, 'num')} value={f.close} disabled={!canEdit && !canPrice} onChange={(e) => set('close', e.target.value)} aria-label="Close price" />
            </Row>
            <Row label="Close time (UTC)">
              {canEdit ? (
                <TimeBox value={f.closedAt} onChange={(v) => setF((x) => ({ ...x, closedAt: v, closeSpan: 1 }))} label="Close time (UTC)" />
              ) : (
                <Shown>{pos.closedAt ? fmtDateTime(pos.closedAt) : '—'}</Shown>
              )}
            </Row>
          </>
        ) : (
          <Row label="Current close rate" wide>
            <Shown>{bookedRate ?? '—'}</Shown>
          </Row>
        )}
        <Row label={closed ? 'Profit' : 'Current profit'}>
          <Shown className={tone(profit)}>{profit == null ? '—' : fmtUsd(profit)}</Shown>
        </Row>
        <Row label="Change in profit">
          <div className="flex min-w-0 flex-col gap-0.5">
            <Shown className={tone(totalChange?.toString())}>
              {change.error ? (
                <span className="text-down">{change.error}</span>
              ) : totalChange === null ? (
                <Spinner />
              ) : (
                <>
                  {totalChange.isZero() ? fmtUsd(0) : signed(totalChange.toString())}
                  {!closed && typedProfit && !profitMove.isZero() && <span className="ml-auto pl-2 text-[11px] whitespace-nowrap text-muted">→ {fmtUsd(typedProfit)}</span>}
                </>
              )}
            </Shown>
            {changeNote && <span className="text-[11px] leading-tight text-muted">{changeNote}</span>}
          </div>
        </Row>
        {!closed && (
          <>
            <Toggled label="Stop loss" on={f.slOn} value={f.sl} disabled={!canTpSl} onOn={(v) => set('slOn', v)} onValue={(v) => set('sl', v)} />
            <Row label="Liq. price">
              <Shown className="text-muted">{pos.liqPrice ?? '—'}</Shown>
            </Row>
            <Toggled label="Take profit" on={f.tpOn} value={f.tp} disabled={!canTpSl} onOn={(v) => set('tpOn', v)} onValue={(v) => set('tp', v)} />
            <div />
            {tpslWrong && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-down/10 px-3 py-2 text-[12px] text-down sm:col-span-2" role="alert">
                <span>
                  As a {sideLabel(f.side)}: {tpslWrong} ({turnRef!.toString()}). It would close the trade at once. Move it or clear it.
                </span>
                {tp && (
                  <button type="button" className="h-7 rounded-md border border-down/40 px-2.5 text-[11px] font-semibold" onClick={() => set('tpOn', false)}>
                    Clear TP
                  </button>
                )}
                {sl && (
                  <button type="button" className="h-7 rounded-md border border-down/40 px-2.5 text-[11px] font-semibold" onClick={() => set('slOn', false)}>
                    Clear SL
                  </button>
                )}
              </div>
            )}
          </>
        )}
        <Row label="Swap">
          <Affixed prefix="$">
            <input className={cx(input, 'num', tone(f.swap))} value={f.swap} disabled={!canSwap} onChange={(e) => set('swap', e.target.value)} aria-label="Swap" />
          </Affixed>
        </Row>
        <Row label="Commission">
          <Affixed prefix="$">
            <input className={cx(input, 'num')} value={f.commission} disabled={!canPrice} onChange={(e) => set('commission', e.target.value)} aria-label="Commission" title={closed ? undefined : 'Close the position first to correct its commission'} />
          </Affixed>
        </Row>
      </div>
      {inst && !closed && <PlainWords inst={inst} side={shown.side} lots={f.qty || (lotsNow?.toString() ?? '')} leverage={String(lev)} entry={shown.entryPrice} quote={quote} free={ctx.summary?.freeMargin ?? null} stopOut={pos.liqPrice ?? null} />}
      {ctx.masterId && copies && <CopiesNotice copies={copies} inst={inst} closed={closed} turned={turned} />}
      {(editing || corrections.length > 0) && !deleting && <WhatSaveDoes closed={closed} preview={change.preview} amount={change.amount} corrections={corrections} editing={editing} tpsl={tpslChanged} lotSize={inst?.lotSize} master={ctx.master} />}
      {closed && onRestore && (
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted">
          Open this trade again as it was, with its close taken back:
          <button type="button" className="h-7 rounded-md border border-line-strong px-2.5 text-[11px] font-semibold text-fg" onClick={onRestore}>
            Restore trade…
          </button>
        </div>
      )}
      {inst && canEdit && closing !== 'chart' && (
        <div role="radiogroup" aria-label="Pick on chart" className="flex flex-wrap items-center gap-1 text-[12px] text-muted">
          Pick on chart:
          {([null, 'open', ...(closed ? (['close'] as const) : [])] as const).map((w) => (
            <button key={w ?? 'off'} type="button" role="radio" aria-checked={picking === w} onClick={() => setPicking(w)} className={cx('h-7 rounded-md border px-2.5 text-[11px] font-semibold', picking === w ? 'border-accent text-accent' : 'border-line text-muted')}>
              {w === null ? 'Off' : w === 'open' ? 'Open price and time' : 'Close price and time'}
            </button>
          ))}
          {picking && <span className="text-[11px]">Click a candle: its price and minute go into the {picking} fields. Drag its line to fine-tune the price.</span>}
        </div>
      )}
      {inst && canEdit && openMoved && fromBox(f.openedAt) && PRICE.test(f.open) && (
        <PointChecks positionId={pos.id} field="open" label="Open" price={f.open} at={fromBox(f.openedAt)} span={f.openSpan} tp={closed ? null : tp} sl={closed ? null : sl} onUseAt={(v) => setF((x) => ({ ...x, openedAt: v, openSpan: 1 }))} />
      )}
      {inst && canEdit && closeMoved && fromBox(f.closedAt) && PRICE.test(f.close) && (
        <PointChecks positionId={pos.id} field="close" label="Close" price={f.close} at={fromBox(f.closedAt)} span={f.closeSpan} openedAt={fromBox(f.openedAt) || pos.openedAt} onUseAt={(v) => setF((x) => ({ ...x, closedAt: v, closeSpan: 1 }))} />
      )}
      {inst && (
        <div className="grid gap-3 lg:grid-cols-2">
          <DeskChart
            symbol={pos.symbol}
            dp={inst.priceDecimals}
            quote={closed ? null : quote}
            lines={chartLines}
            defaultTf="1h"
            range={closed && pos.closedAt ? range : undefined}
            marks={[
              { t: openedMs, label: 'Opened', tone: 'accent' },
              ...(closedMs != null ? [{ t: closedMs, label: 'Closed', tone: 'up' as const, below: true }] : []),
              ...(closing === 'chart' && exitMs != null ? [{ t: exitMs, label: 'Close', tone: 'down' as const, below: true }] : []),
            ]}
            pick={picking ? { at: pickAt, onPick: picked } : undefined}
            onDrag={(!closed && canTpSl) || picking ? dragged : undefined}
            onCandles={closed ? setCandles : undefined}
            accountId={ctx.accountId}
            followers={ctx.master}
            height={230}
          />
          {closed ? (
            <ClosedResult pos={typedClosed} booked={booked} inst={inst} candles={candles} />
          ) : (
            <OpenPositionNow pos={shown} inst={inst} quote={quote} tp={tp} sl={sl} />
          )}
        </div>
      )}
      {inst &&
        (closed ? (
          <OpenRateImpact
            inst={inst}
            side={shown.side}
            perPoint={closedPerPoint(typedClosed, inst, booked)}
            exit={PRICE.test(f.close) ? D.from(f.close) : pos.closePrice ? D.from(pos.closePrice) : null}
            exitLabel="closed at"
            original={D.from(pos.entryPrice)}
            typed={PRICE.test(f.open) ? D.from(f.open) : null}
            master={ctx.master}
          />
        ) : (
          <OpenRateImpact
            inst={inst}
            side={shown.side}
            perPoint={D.from(shown.qty).mul(usdPerPoint(inst.contractSize, quote?.fx))}
            exit={liveRate ? D.from(liveRate) : null}
            exitLabel="at today's close rate"
            original={D.from(pos.entryPrice)}
            typed={PRICE.test(f.open) ? D.from(f.open) : null}
            master={ctx.master}
          />
        ))}
      {details && (
        <div className="flex flex-col gap-2 rounded-lg border border-line bg-bg p-2.5">
          <div className="flex flex-wrap items-center gap-2 text-[12px] text-muted">
            <span>Cancel this trade: its P&L is reversed on the {ctx.master ? 'master’s balance (its followers’ copies keep theirs)' : 'client’s balance'}.</span>
            <select className={cx(input, 'h-8 w-auto')} value={f.reasonCode} onChange={(e) => set('reasonCode', e.target.value)} aria-label="Reason">
              {REASONS.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <textarea className={cx(input, 'h-16 py-2')} placeholder="Comment / evidence: feed snapshot, order log, rate table, incident" value={f.evidence} onChange={(e) => set('evidence', e.target.value)} aria-label="Comment / evidence" />
          <input className={input} placeholder={ctx.master ? 'Note, kept with the trade’s history (optional)' : "Note on the client's statement"} value={f.clientNote} onChange={(e) => set('clientNote', e.target.value)} aria-label={ctx.master ? 'Note' : 'Client note'} />
        </div>
      )}
      {(editing || corrections.length > 0) && !deleting && (closed || corrections.length > 0) && (
        <input className={input} placeholder={ctx.master ? 'Note, kept with the trade’s history (optional)' : "Note on the client's statement (optional)"} value={f.clientNote} onChange={(e) => set('clientNote', e.target.value)} aria-label={ctx.master ? 'Note' : 'Client note'} />
      )}
      {closing === 'chart' && inst && (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-down/60 p-2.5">
          <span className="text-[12px] font-semibold text-fg">Close at a past price and time</span>
          <span className="text-[11px] text-muted">Click a candle on the chart (or type below); drag the Close line to fine-tune the price. The trade closes with its result at that price, at that minute.</span>
          {ctx.master && (
            <span className="text-[11px] text-muted">
              Each follower’s copy closes the same share at that price and minute too; a copy opened after that minute closes at today’s price.
            </span>
          )}
          <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Row label="Close time (UTC)">
              <TimeBox value={f.exitAt} onChange={(v) => setF((x) => ({ ...x, exitAt: v, exitSpan: 1 }))} label="Close time (UTC)" min={toBox(pos.openedAt)} />
            </Row>
            <Row label="Close price">
              <input className={cx(input, 'num')} value={f.exitPrice} onChange={(e) => set('exitPrice', e.target.value)} aria-label="Close price" placeholder="Click a candle" />
            </Row>
          </div>
          {exitIso && exitPx && (
            <PointChecks positionId={pos.id} field="close" label="Close" price={exitPx} at={exitIso} span={f.exitSpan} openedAt={pos.openedAt} onUseAt={(v) => setF((x) => ({ ...x, exitAt: v, exitSpan: 1 }))} />
          )}
          {exitIso && exitMs! <= Date.parse(pos.openedAt) && <span className="text-[12px] text-down">The close must be after the trade opened ({utcLabel(Date.parse(pos.openedAt))}).</span>}
          {exitResult && (
            <span className="text-[12px] text-muted">
              Result at {exitPx}: <b className={cx('num', tone(exitResult.toString()))}>{signed(exitResult.toFixed(2))}</b> before swap and the close fee (whole position)
            </span>
          )}
        </div>
      )}
      {fail && <p className="m-0 text-xs text-down">{fail}</p>}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!closed && ctx.canTrade && !closing && (
          <button type="button" className={cx(btn, 'mr-auto border border-down/40 text-down')} disabled={busy} onClick={() => setClosing('live')}>
            Close position…
          </button>
        )}
        {closing === 'live' && (
          <span className="mr-auto flex flex-wrap items-center gap-1">
            <span className="text-[12px] text-muted">Close at the live price:</span>
            {['0.25', '0.5', '1'].map((fr) => (
              <button key={fr} type="button" className={cx(btn, 'h-8 border border-line-strong', fr === '1' && 'border-down/40 text-down')} disabled={busy} onClick={() => void closeAt(fr)}>
                {fr === '1' ? 'All' : `${Number(fr) * 100}%`}
              </button>
            ))}
            {ctx.canBackdate && inst && inst.assetClass !== 'MEME' && (
              <button
                type="button"
                className={cx(btn, 'h-8 border border-line-strong')}
                title="Close at a price and minute picked on the chart"
                onClick={() => {
                  setClosing('chart');
                  setPicking('exit');
                }}
              >
                At a past price…
              </button>
            )}
            <button type="button" className={cx(btn, 'h-8 text-muted')} onClick={() => setClosing(false)}>
              Back
            </button>
          </span>
        )}
        {closing === 'chart' && (
          <span className="mr-auto flex flex-wrap items-center gap-1">
            <span className="num text-[12px] text-muted">Close at {exitPx && exitMs ? `${exitPx} · ${utcLabel(exitMs)}` : 'the picked point'}:</span>
            {['0.25', '0.5', '1'].map((fr) => (
              <button key={fr} type="button" className={cx(btn, 'h-8 border border-line-strong', fr === '1' && 'border-down/40 text-down')} disabled={busy || !exitReady} onClick={() => void closeFromChart(fr)}>
                {fr === '1' ? 'All' : `${Number(fr) * 100}%`}
              </button>
            ))}
            <button
              type="button"
              className={cx(btn, 'h-8 text-muted')}
              onClick={() => {
                setClosing('live');
                setPicking(null);
              }}
            >
              Back
            </button>
          </span>
        )}
        {closed && ctx.canCorrect && (
          <button
            type="button"
            className={cx(btn, 'mr-auto border', deleting ? 'border-down bg-down text-white' : 'border-down/40 text-down')}
            disabled={busy || (deleting && !detailsOk)}
            onClick={() => (deleting ? void cancelTrade() : setDeleting(true))}
          >
            {deleting ? 'Confirm: cancel trade' : 'Delete…'}
          </button>
        )}
        {!deleting && (
          <button type="button" className={cx(btn, 'bg-accent text-white')} disabled={!canSave} onClick={() => void save()}>
            {busy ? <Spinner /> : '✓ Save'}
          </button>
        )}
        <button type="button" className={cx(btn, 'border border-line-strong')} onClick={() => (deleting ? setDeleting(false) : onClose())}>
          ✕ Cancel
        </button>
      </div>
    </Modal>
  );
}

/** A pending limit / stop order, as the Pending tab lists it (desk/accounts.ts accountOrders). */
export interface Ord {
  id: string;
  tradeId: number | null;
  symbol: string;
  side: 'long' | 'short';
  type: string;
  notional: string | null;
  lots: string | null;
  leverage: number;
  limitPrice: string | null;
  stopPrice: string | null;
  tpPrice: string | null;
  slPrice: string | null;
  /** Margin and open fee the order holds until it fills. */
  holdAmount?: string | null;
  createdAt: string;
  placedBy: string | null;
}

/** "Buy limit", "Sell stop". */
const orderLabel = (side: 'long' | 'short', type: string) => `${sideLabel(side)} ${type}`;
/** When a pending order fills: a buy limit when the Ask falls to its price, a buy stop when it rises to it (a sell: the Bid). */
const fillsWhen = (side: 'long' | 'short', type: string, price: string) => `fills when the ${side === 'long' ? 'Ask' : 'Bid'} ${(side === 'long') === (type === 'limit') ? 'falls' : 'rises'} to ${price}`;

/**
 * Edit pending order (the Pending tab): its type (buy / sell), price, amount, leverage, TP and SL, on a
 * client's account or a master's. Limit or stop follows from the type and the price against the live
 * price, as when it was placed, and the margin it holds is worked out again. Cancel order is here too.
 */
export function OrderDialog({ order: o, onClose }: { order: Ord; onClose: () => void }) {
  const ctx = useDesk();
  const inst = useInstrument(o.symbol);
  const quote = useQuote(o.symbol);
  const byLots = o.lots != null;
  const placedAt = o.limitPrice ?? o.stopPrice ?? '';
  const amountNow = byLots ? D.from(o.lots!).toString() : D.from(o.notional ?? 0).toFixed(2);
  const [f, setF] = useState({ side: o.side, price: placedAt, amount: amountNow, leverage: String(o.leverage), slOn: !!o.slPrice, sl: o.slPrice ?? '', tpOn: !!o.tpPrice, tp: o.tpPrice ?? '' });
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const set = (k: keyof typeof f, v: string | boolean) => {
    setF((x) => ({ ...x, [k]: v }));
    setFail(null);
  };
  const marks: DeskMark[] = useMemo(() => [{ t: Date.parse(o.createdAt), label: 'Placed', tone: 'accent' }], [o.createdAt]);
  if (!ctx) return null;
  const no = o.tradeId != null ? `order ${fmtTradeId(o.tradeId)}` : o.id.slice(0, 8);
  const size = byLots ? `${amountNow} lots` : fmtUsd(amountNow);
  const canTrade = ctx.canTrade;
  const price = PRICE.test(f.price) && D.from(f.price).isPos() ? f.price : null;
  const amount = PRICE.test(f.amount) && D.from(f.amount).isPos() ? f.amount : null;
  const lev = /^\d{1,4}$/.test(f.leverage.trim()) && Number(f.leverage) >= 1 ? Number(f.leverage) : null;
  const tp = f.tpOn && f.tp.trim() ? f.tp.trim() : null;
  const sl = f.slOn && f.sl.trim() ? f.sl.trim() : null;
  const q = quote ? { bid: D.from(quote.bid), ask: D.from(quote.ask) } : null;
  const ref = f.side === 'long' ? 'Ask' : 'Bid';
  const refNow = q ? (f.side === 'long' ? q.ask : q.bid) : null;
  // Limit or stop: from the type and the price against the live price, as the server works it out on Save.
  const kind = q && price ? pendingKind(f.side, q, price) : null;
  const type = kind ?? o.type;
  const pv = inst ? usdPerPoint(inst.contractSize, quote?.fx) : null;
  const notional = inst && price && amount ? (byLots ? notionalOf(lotsToQty(amount, inst.lotSize), price, pv!) : D.from(amount)) : null;
  const hold = inst && notional && lev ? initialMargin(notional, lev).add(openFeeFor(notional, inst.feeRate, inst.feeFlat)) : null;
  const holdNow = o.holdAmount ? D.from(o.holdAmount) : null;
  const extra = hold && holdNow ? hold.sub(holdNow) : null;
  const levRange: LeverageRange | null = inst
    ? { min: inst.minLeverage, max: inst.maxLeverage, cap: ctx.leverageCap, capOf: ctx.master ? `${ctx.summary?.label ?? 'this master'}’s` : 'this client’s', now: o.leverage }
    : null;

  // What changes, field by field (the server checks it all again).
  const sameNum = (a: string | null, b: string | null) => (a == null || b == null ? a === b : PRICE.test(a) && PRICE.test(b) && D.from(a).eq(D.from(b)));
  const changes: { label: string; from: string; to: string }[] = [];
  const body: Record<string, string | number | null> = {};
  if (f.side !== o.side) (body.side = f.side), changes.push({ label: 'Type', from: sideLabel(o.side), to: sideLabel(f.side) });
  if (type !== o.type) changes.push({ label: 'Order', from: orderLabel(o.side, o.type), to: orderLabel(f.side, type) });
  if (!sameNum(f.price, placedAt)) (body.price = f.price), changes.push({ label: 'Price', from: placedAt, to: f.price || '—' });
  if (!sameNum(f.amount, amountNow)) (body[byLots ? 'lots' : 'notional'] = f.amount), changes.push({ label: byLots ? 'Lots' : 'Amount', from: byLots ? amountNow : fmtUsd(amountNow), to: byLots ? f.amount || '—' : amount ? fmtUsd(amount) : '—' });
  if (f.leverage.trim() !== String(o.leverage)) (body.leverage = lev ?? f.leverage), changes.push({ label: 'Leverage', from: `1:${o.leverage}`, to: `1:${f.leverage}` });
  if (!sameNum(tp, o.tpPrice)) (body.tpPrice = tp), changes.push({ label: 'Take profit', from: o.tpPrice ?? '—', to: tp ?? '—' });
  if (!sameNum(sl, o.slPrice)) (body.slPrice = sl), changes.push({ label: 'Stop loss', from: o.slPrice ?? '—', to: sl ?? '—' });
  const resized = 'side' in body || 'lots' in body || 'notional' in body || 'leverage' in body;

  // Why Save is off, in the dealer's words.
  const problems: string[] = [];
  if (!price) problems.push('Type the order price.');
  else if (q && !kind) problems.push(`The price must differ from the current ${ref} (${refNow!.toString()}).`);
  if (!amount) problems.push(byLots ? 'Type the amount in lots.' : 'Type the amount in USD.');
  else if (inst && byLots && D.from(amount).lt(inst.minLots)) problems.push(`Minimum volume is ${inst.minLots} lots.`);
  else if (inst && byLots && !D.from(amount).div(inst.lotStep).round(0).mul(inst.lotStep).eq(D.from(amount))) problems.push(`Volume must be a multiple of ${inst.lotStep} lots.`);
  else if (inst && notional && notional.lt(inst.minNotional)) problems.push(`Minimum order is ${fmtUsd(inst.minNotional)}.`);
  if ((tp && !PRICE.test(tp)) || (sl && !PRICE.test(sl))) problems.push('TP and SL must be prices.');
  else if (price && (tp || sl)) {
    const wrong = validateTpSl(f.side, D.from(price), tp ? D.from(tp) : null, sl ? D.from(sl) : null);
    if (wrong) problems.push(`As a ${orderLabel(f.side, type)} at ${price}: ${wrong}.`);
  }
  const levBad = levRange && 'leverage' in body ? leverageProblem(f.leverage, levRange) : null;
  if (extra && extra.isPos() && ctx.summary && extra.gt(ctx.summary.freeMargin)) problems.push(`Not enough free margin: it needs ${fmtUsd(extra)} more, ${fmtUsd(ctx.summary.freeMargin)} is free.`);
  if (inst?.assetClass === 'MEME' && resized) problems.push('A meme coin order takes a new price, TP and SL only; cancel it and place a new one to change the rest.');
  const canSave = canTrade && !busy && changes.length > 0 && !problems.length && !levBad;

  // Done: the pop-up closes, the page reloads and the corner note says what was done; a refusal or an
  // error keeps it open with the form as typed (DEALER_POPUP_CLOSE_PLAN.md).
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setFail(null);
    try {
      const line = await fn();
      ctx.refresh();
      deskNotice({ title: `${o.tradeId != null ? `Order ${fmtTradeId(o.tradeId)}` : `Order ${o.id.slice(0, 8)}`} · ${orderLabel(o.side, o.type)} ${size} ${o.symbol}`, lines: [line] });
      onClose();
    } catch (e) {
      setFail(err(e));
    } finally {
      setBusy(false);
    }
  };
  const save = () =>
    run(async () => {
      const r = await api.post<{ side: 'long' | 'short'; type: string; limitPrice: string; lots: string | null; notional: string | null; symbol: string }>(`/api/admin/desk/orders/${o.id}/edit`, body);
      return `Order saved: ${orderLabel(r.side, r.type)} ${r.lots ? `${r.lots} lots` : fmtUsd(r.notional ?? 0)} ${r.symbol} at ${r.limitPrice}`;
    });
  const cancelOrder = () =>
    run(async () => {
      await api.post(`/api/admin/desk/orders/${o.id}/cancel`, {});
      return 'Order cancelled: its held margin is released';
    });
  const dragged = (id: string, p: string) => {
    if (id === 'order') set('price', p);
    if (id === 'tp') setF((x) => ({ ...x, tpOn: true, tp: p }));
    if (id === 'sl') setF((x) => ({ ...x, slOn: true, sl: p }));
  };
  const lines: DeskLine[] = [
    { id: 'order', price, label: orderLabel(f.side, type), tone: 'accent', drag: canTrade },
    { id: 'tp', price: tp && PRICE.test(tp) ? tp : null, label: 'TP', tone: 'up', dashed: true, drag: canTrade },
    { id: 'sl', price: sl && PRICE.test(sl) ? sl : null, label: 'SL', tone: 'down', dashed: true, drag: canTrade },
  ];
  const title = `Edit pending order: ${no} · ${orderLabel(o.side, o.type)} ${size} ${o.symbol} at ${placedAt}`;
  const heading = (
    <>
      Edit pending order: {no} · <span className={o.side === 'long' ? 'text-up' : 'text-down'}>{orderLabel(o.side, o.type)}</span> {size} <InstrumentLabel symbol={o.symbol} size={20} /> at {placedAt}
    </>
  );
  return (
    <Modal open onClose={onClose} title={title} heading={heading} width={inst ? 1040 : 760}>
      <AccountStrip s={ctx.summary} after={extra && !extra.isZero() ? { margin: extra.toString(), label: 'after this edit' } : undefined} />
      <Section title="Order" />
      <>
          <div className="grid gap-4 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)]">
            <div className="grid content-start gap-2">
              <Row label="Order ID">
                <Shown>
                  <TradeId id={o.tradeId} className="text-[13px]" />
                  <span className="ml-auto pl-2 text-[11px] text-muted">{o.id.slice(0, 8)}</span>
                </Shown>
              </Row>
              <Row label="Instrument">
                <Shown>{o.symbol}</Shown>
              </Row>
              <Row label="Type">
                {canTrade ? (
                  <select className={cx(input, sideTone(f.side))} value={f.side} onChange={(e) => set('side', e.target.value)} aria-label="Type">
                    <option value="long" className="text-up">
                      {sideLabel('long')}
                    </option>
                    <option value="short" className="text-down">
                      {sideLabel('short')}
                    </option>
                  </select>
                ) : (
                  <Shown className={sideTone(o.side)}>{sideLabel(o.side)}</Shown>
                )}
              </Row>
              <Row label="Price">
                <Affixed suffix={price ? `${orderLabel(f.side, type)}: ${fillsWhen(f.side, type, price)}${refNow ? ` (${ref} now ${refNow.toString()})` : ''}` : undefined}>
                  <input className={cx(input, 'num')} value={f.price} disabled={!canTrade} onChange={(e) => set('price', e.target.value)} aria-label="Order price" />
                </Affixed>
              </Row>
              <Row label={byLots ? 'Amount (lots)' : 'Amount (USD)'}>
                <Affixed prefix={byLots ? undefined : '$'} suffix={inst && notional ? `${byLots && amount ? `= ${fmtQty(lotsToQty(amount, inst.lotSize))} ${inst.unit} · ` : ''}${fmtUsd(notional, 0)} position at ${price}` : undefined}>
                  <input className={cx(input, 'num')} value={f.amount} disabled={!canTrade} onChange={(e) => set('amount', e.target.value)} aria-label={byLots ? 'Amount in lots' : 'Amount in USD'} />
                </Affixed>
              </Row>
              <Row label="Leverage">
                {canTrade && levRange ? (
                  <LeverageField value={f.leverage} onChange={(v) => set('leverage', v)} range={levRange} defaultLev={inst?.defaultLeverage} margin={{ before: holdNow, after: hold }} />
                ) : (
                  <Affixed prefix="1:">
                    <Shown>{o.leverage}</Shown>
                  </Affixed>
                )}
              </Row>
              <Toggled label="Stop loss" on={f.slOn} value={f.sl} disabled={!canTrade} onOn={(v) => set('slOn', v)} onValue={(v) => set('sl', v)} />
              <Toggled label="Take profit" on={f.tpOn} value={f.tp} disabled={!canTrade} onOn={(v) => set('tpOn', v)} onValue={(v) => set('tp', v)} />
              <Row label="Margin held">
                <Shown>
                  {holdNow ? fmtUsd(holdNow) : '—'}
                  {hold && holdNow && !hold.eq(holdNow) && <span className={cx('ml-auto pl-2 text-[11px] whitespace-nowrap', extra!.isPos() ? 'text-down' : 'text-up')}>→ {fmtUsd(hold)}</span>}
                </Shown>
              </Row>
              <Row label="Placed">
                <Shown className="text-muted">
                  {fmtDateTime(o.createdAt)}
                  {o.placedBy ? ` · ${o.placedBy}` : ''}
                </Shown>
              </Row>
            </div>
            {inst ? (
              <DeskChart symbol={o.symbol} dp={inst.priceDecimals} quote={quote} lines={lines} marks={marks} onDrag={canTrade ? dragged : undefined} height={300} accountId={ctx.accountId} followers={ctx.master} />
            ) : (
              <div className="grid h-[240px] place-items-center rounded-lg border border-line text-[12px] text-muted">Loading the instrument…</div>
            )}
          </div>
          {ctx.master && (
            <p className="m-0 text-[11.5px] text-muted">Not copied yet: when the order fills, each follower gets a copy sized to their equity then, as for any trade on {ctx.summary?.label ?? 'this master'}.</p>
          )}
          {ctx.master && inst && byLots && amount && price && lev && <MasterCopies accountId={ctx.accountId} symbol={o.symbol} side={f.side} lots={amount} leverage={String(lev)} price={price} />}
          {changes.length > 0 && (
            <div className="flex flex-col gap-0.5 rounded-lg border border-line bg-bg px-3 py-2 text-[12.5px]">
              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted">What Save will do</span>
              {changes.map((c) => (
                <span key={c.label}>
                  {c.label} <s className="text-muted">{c.from}</s> → <b>{c.to}</b>
                </span>
              ))}
              {hold && holdNow && !hold.eq(holdNow) && (
                <span>
                  Margin held <s className="text-muted">{fmtUsd(holdNow)}</s> → <b>{fmtUsd(hold)}</b>, {extra!.isPos() ? `${fmtUsd(extra!)} more taken from` : `${fmtUsd(extra!.neg())} back to`} the free margin.
                </span>
              )}
              <span className="text-muted">No money moves now: the order fills at its price{ctx.master ? ', and is copied then' : ''}.</span>
            </div>
          )}
          {changes.length > 0 && problems.length > 0 && (
            <ul className="m-0 flex list-none flex-col gap-1 rounded-lg bg-down/10 p-2.5 text-[12.5px] text-down" role="alert">
              {problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ul>
          )}
          {fail && <p className="m-0 text-xs text-down">{fail}</p>}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {canTrade && (
              <button
                type="button"
                className={cx(btn, 'mr-auto border', cancelling ? 'border-down bg-down text-white' : 'border-down/40 text-down')}
                disabled={busy}
                onClick={() => (cancelling ? void cancelOrder() : setCancelling(true))}
              >
                {cancelling ? 'Confirm: cancel order' : 'Cancel order…'}
              </button>
            )}
            {canTrade && !cancelling && (
              <button type="button" className={cx(btn, 'bg-accent text-white')} disabled={!canSave} onClick={() => void save()}>
                {busy ? <Spinner /> : '✓ Save'}
              </button>
            )}
            <button type="button" className={cx(btn, 'border border-line-strong')} onClick={() => (cancelling ? setCancelling(false) : onClose())}>
              {cancelling ? 'Back' : '✕ Close'}
            </button>
          </div>
      </>
    </Modal>
  );
}

/** Open a position for account: instrument, amount, current or a specific rate, SL / TP, Sell / Buy at the live price. */
const LAST_SYMBOL = 'desk-open-symbol';
const lastSymbol = () => {
  try {
    return localStorage.getItem(LAST_SYMBOL) ?? '';
  } catch {
    return '';
  }
};

export function OpenPositionDialog({ ctx, onClose }: { ctx: DeskCtx; onClose: () => void }) {
  const [f, setF] = useState({ symbol: lastSymbol(), lots: '', leverage: '', mode: 'current' as 'current' | 'specific' | 'chart', price: '', at: '', entry: '', span: 1, slOn: false, sl: '', tpOn: false, tp: '', transfer: false });
  const inst = useInstrument(f.symbol.trim().toUpperCase());
  const q = useQuote(inst?.symbol ?? '');
  const ticker = useTicker(inst?.symbol ?? '');
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  // The server's refusal of the last click, with its note (DEALER_OPEN_REFUSALS_PLAN.md); any change to the form clears it.
  const [refused, setRefused] = useState<{ side: 'long' | 'short'; help: DeskRefusal } | null>(null);
  // Which side the estimate and chart show; hovering Sell or Buy switches it.
  const [estSide, setEstSide] = useState<'long' | 'short'>('long');
  const [stopOut, setStopOut] = useState<string | null>(null);
  const set = (k: keyof typeof f, v: string | boolean) => {
    setF((x) => ({ ...x, [k]: v }));
    setFail(null);
    setRefused(null);
  };
  useEffect(() => {
    // Each instrument opens at its default leverage and its minimum volume (0.01 lot on most), like the client ticket.
    if (inst) setF((x) => ({ ...x, leverage: String(inst.defaultLeverage), lots: D.from(inst.minLots).toFixed(Math.max(0, (inst.lotStep.split('.')[1] ?? '').length)) }));
    if (inst) {
      try {
        localStorage.setItem(LAST_SYMBOL, inst.symbol);
      } catch {
        /* private window: start from the default next time */
      }
    }
  }, [inst?.symbol]); // eslint-disable-line react-hooks/exhaustive-deps
  // Open straight on the full pop-up (chart and estimate): the last instrument used here, else EURUSD,
  // else the first one, so the dealer never sees an empty form first.
  const instruments = useInstruments();
  useEffect(() => {
    if (inst || !instruments.length) return;
    const live = instruments.filter((i) => i.enabled !== false);
    const pick = live.find((i) => i.symbol === f.symbol.trim().toUpperCase()) ?? live.find((i) => i.symbol === 'EURUSD') ?? live[0];
    if (pick && pick.symbol !== f.symbol) setF((x) => ({ ...x, symbol: pick.symbol }));
  }, [instruments, inst]); // eslint-disable-line react-hooks/exhaustive-deps
  // Open positions: a market order against one on the other side needs less margin than its own.
  const held = useLoad<{ data: { side: 'long' | 'short'; symbol: string }[] }>(`/api/admin/desk/accounts/${ctx.accountId}/positions?status=open`, [ctx.accountId]);

  const units = inst && PRICE.test(f.lots) ? D.from(f.lots).mul(inst.lotSize).toString() : null;
  const specific = f.mode === 'specific';
  const chart = f.mode === 'chart';
  const atIso = chart ? fromBox(f.at) : '';
  const atMs = atIso ? Date.parse(atIso) : null;
  const entry = chart && PRICE.test(f.entry) ? f.entry : null;
  const check = useEntryCheck(chart && inst ? { accountId: ctx.accountId, symbol: inst.symbol, side: estSide, lots: f.lots, at: atIso, price: entry, span: f.span, tp: f.tpOn ? f.tp : '', sl: f.slOn ? f.sl : '' } : null);
  // The instrument's range under the client's own cap (or the master's), as the order path checks it.
  const levRange: LeverageRange | null = inst ? { min: inst.minLeverage, max: inst.maxLeverage, cap: ctx.leverageCap, capOf: ctx.master ? `${ctx.summary?.label ?? 'this master'}’s` : 'this client’s' } : null;
  // Filled in: the leverage box says itself what is wrong with a leverage, so the checks below start after it.
  const filled =
    !!inst && !!units && D.from(f.lots).isPos() && /^\d+$/.test(f.leverage) && !(levRange && leverageProblem(f.leverage, levRange)) && (!specific || PRICE.test(f.price)) && (!chart || (!!atIso && !!entry));
  const ready = filled && !busy;
  const picked = (t: number, price: string, span: number) => setF((x) => ({ ...x, at: toBox(new Date(t).toISOString()), entry: price, span }));
  // A specific rate is a pending order: a limit below the market for a buy (above for a sell), a stop otherwise.
  const pendingFor = (side: 'long' | 'short'): 'limit' | 'stop' => {
    const market = side === 'long' ? q?.ask : q?.bid;
    return !market || (side === 'long') === D.from(f.price).lt(market) ? 'limit' : 'stop';
  };
  const place = async (side: 'long' | 'short') => {
    if (!inst) return;
    setBusy(true);
    setFail(null);
    setRefused(null);
    try {
      const type = !specific ? 'market' : pendingFor(side);
      const r = await api.post<{ order: { status: string; tradeId: number | null }; position: { entryPrice: string; openedAt: string } | null }>(`/api/admin/desk/accounts/${ctx.accountId}/orders`, {
        symbol: inst.symbol,
        side,
        type,
        lots: f.lots,
        leverage: Number(f.leverage),
        limitPrice: specific ? f.price : null,
        tpPrice: f.tpOn && f.tp ? f.tp : null,
        slPrice: f.slOn && f.sl ? f.sl : null,
        ...(f.transfer ? { transfer: true } : {}),
        ...(chart ? { entry: { price: entry, at: atIso } } : {}),
      });
      // Placed: the pop-up closes and the page reloads; the corner note says what was done (DEALER_POPUP_CLOSE_PLAN.md).
      ctx.refresh();
      deskNotice({
        title: `Account ${s?.label ?? ctx.accountId.slice(0, 8)}${r.order.tradeId != null ? ` · order ${fmtTradeId(r.order.tradeId)}` : ''}`,
        lines: [
          r.position && chart
            ? `${sideLabel(side)} ${f.lots} lot ${inst.symbol} opened at ${r.position.entryPrice}, ${utcLabel(Date.parse(r.position.openedAt))}.`
            : r.position
              ? `${sideLabel(side)} ${f.lots} lot ${inst.symbol} filled at ${r.position.entryPrice}.`
              : `${sideLabel(side)} ${type} order placed at ${f.price} (${r.order.status}).`,
          ...(f.transfer ? ['Now open it (Edit) to set its previous broker’s dates and prices.'] : []),
        ],
      });
      onClose();
    } catch (e) {
      const help = e instanceof ApiError ? (e.data?.help as DeskRefusal | undefined) : undefined;
      if (help) setRefused({ side, help });
      else setFail(err(e));
    } finally {
      setBusy(false);
    }
  };
  const dp = inst?.priceDecimals ?? 2;
  const px = (x: string) => fmt(x, dp);
  const at = (side: 'long' | 'short') => {
    if (chart) return entry ? `${px(entry)}${atMs ? ` · ${shortWhen(atMs)}` : ''}` : '—';
    if (specific) return PRICE.test(f.price) ? px(f.price) : '—';
    const live = side === 'long' ? q?.ask : q?.bid;
    return live ? px(live) : '—';
  };
  const sideName = (side: 'long' | 'short') => (specific && PRICE.test(f.price) && q ? `${sideLabel(side)} ${pendingFor(side) === 'limit' ? 'Limit' : 'Stop'}` : sideLabel(side));
  const tp = f.tpOn && PRICE.test(f.tp) ? f.tp : null;
  const sl = f.slOn && PRICE.test(f.sl) ? f.sl : null;
  const lines: DeskLine[] = [
    { id: 'tp', price: tp, label: 'TP', tone: 'up', dashed: true, drag: true },
    { id: 'sl', price: sl, label: 'SL', tone: 'down', dashed: true, drag: true },
    ...(specific && PRICE.test(f.price) ? [{ id: 'entry', price: f.price, label: 'Order', tone: 'accent' as const }] : []),
    ...(entry ? [{ id: 'pick', price: entry, label: 'Entry', tone: 'accent' as const, drag: true }] : []),
    { id: 'stop', price: stopOut, label: 'Stop-out', tone: 'warn', dashed: true },
  ];
  const marks: DeskMark[] = useMemo(() => (atMs ? [{ t: atMs, label: 'Entry', tone: 'accent' }] : []), [atMs]);
  const dragged = (id: string, price: string) => {
    if (id === 'tp') setF((x) => ({ ...x, tpOn: true, tp: price }));
    if (id === 'sl') setF((x) => ({ ...x, slOn: true, sl: price }));
    if (id === 'pick') setF((x) => ({ ...x, entry: price }));
    setRefused(null);
  };
  const s = ctx.summary;
  const entryNow = entry ?? (specific && PRICE.test(f.price) ? f.price : estSide === 'long' ? (q?.ask ?? null) : (q?.bid ?? null));
  const position = inst && units && entryNow ? notionalOf(units, entryNow, usdPerPoint(inst.contractSize, q?.fx)) : null;
  const newMargin = position && /^\d+$/.test(f.leverage) && Number(f.leverage) > 0 ? initialMargin(position, Number(f.leverage)) : null;

  // The server's checks, run here as the dealer types: Sell / Buy stay off until the trade can open.
  const sides = new Set((held.data?.data ?? []).filter((p) => p.symbol === inst?.symbol).map((p) => p.side));
  const lim = ctx.limits;
  const open = ticker ? ticker.open : inst ? isMarketOpen(inst.tradingHours) : true;
  const facts: DeskOpenFacts | null = inst
    ? {
        inst,
        pointValue: usdPerPoint(inst.contractSize, q?.fx),
        bid: q?.bid ?? null,
        ask: q?.ask ?? null,
        open,
        opensAt: open ? null : (nextMarketOpen(inst.tradingHours)?.getTime() ?? null),
        // A master is a paper (demo) account: the live-price pause doesn't stop it.
        paused: !ctx.master && !!ticker?.paused,
        freeMargin: s?.freeMargin ?? null,
        offsets: specific ? undefined : { long: sides.has('short'), short: sides.has('long') },
        client: lim?.client ?? null,
        master: lim?.master ?? null,
      }
    : null;
  const form: DeskOpenForm = { lots: f.lots, leverage: f.leverage, price: chart ? entry : specific ? f.price : null, tp: f.tpOn ? f.tp : null, sl: f.slOn ? f.sl : null };
  // Until the instrument's first quote arrives (a moment after picking it) there is nothing to check yet.
  const checks = facts && filled && q ? checkDeskOpen(form, facts) : { long: null, short: null };
  const both = checks.long && checks.short;
  const note = refused
    ? { refusal: refused.help, side: refused.side as 'long' | 'short' | null, server: true }
    : both
      ? { refusal: (checks[estSide] ?? checks.long)!, side: null, server: false }
      : checks.long || checks.short
        ? { refusal: (checks.long ?? checks.short)!, side: (checks.long ? 'long' : 'short') as 'long' | 'short' | null, server: false }
        : null;
  const bad = (k: DeskRefusal['field']) => note?.refusal.field === k;
  const apply = (x: DeskFix) => {
    if (x.kind === 'lots') set('lots', x.value);
    else if (x.kind === 'leverage') set('leverage', String(x.value));
    else if (x.kind === 'current_rate') set('mode', 'current');
    else if (x.kind === 'clear') {
      setF((v) => (x.field === 'tp' ? { ...v, tpOn: false, tp: '' } : { ...v, slOn: false, sl: '' }));
      setRefused(null);
    } else if (x.kind === 'link' && x.to === 'money') {
      // The Money action sits next to Open position on the account page.
      onClose();
      const b = document.getElementById(MONEY_BUTTON);
      if (b && b.getAttribute('aria-expanded') !== 'true') b.click();
      b?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  };
  const details = note
    ? JSON.stringify(
        {
          code: note.refusal.code,
          refusal: `${note.refusal.title}. ${note.refusal.text}`,
          account: s?.label ?? ctx.accountId,
          accountId: ctx.accountId,
          instrument: inst?.symbol,
          side: note.side,
          lots: f.lots,
          leverage: f.leverage,
          rate: f.mode,
          price: form.price ?? null,
          tp: form.tp ?? null,
          sl: form.sl ?? null,
          at: new Date().toISOString(),
        },
        null,
        2,
      )
    : '';

  const mode = chart ? `Opens at the entry picked on the chart${ctx.master ? ', and so do the copies' : ''}` : specific ? `Pending order${PRICE.test(f.price) ? ` · fills at ${px(f.price)}` : ''}` : 'Market execution';
  const masterLine = lim?.master ? ` · ${lim.master.name}'s max order ${lim.master.maxOrder ? fmtUsd(lim.master.maxOrder, 0) : 'none'}` : '';
  const footer = (
    <div className="flex flex-col gap-3">
      {note && <RefusalNote refusal={note.refusal} side={note.side} server={note.server} crmId={lim?.client?.crmId ?? null} details={details} onFix={apply} />}
      {ctx.master && inst && filled && (
        <MasterCopies accountId={ctx.accountId} symbol={inst.symbol} side={estSide} lots={f.lots} leverage={f.leverage} price={chart ? entry : specific ? f.price : null} at={chart ? atIso : null} />
      )}
      {fail && (
        <p className="m-0 text-[12.5px] text-down" role="status">
          {fail}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate text-[10.5px] font-semibold tracking-wide text-muted uppercase">
            {mode}
            {masterLine}
          </span>
          <span className="num truncate text-sm font-semibold">
            {inst && PRICE.test(f.lots) ? D.from(f.lots).toFixed(Math.max(0, (inst.lotStep.split('.')[1] ?? '').length)) : '—'} lots {inst ? <InstrumentLabel symbol={inst.symbol} size={16} className="gap-1.5" /> : ''}
            {chart && entry ? ` @ ${px(entry)}` : specific && PRICE.test(f.price) ? ` @ ${px(f.price)}` : ''}
          </span>
          <span className="num truncate text-xs text-muted">
            {position && newMargin && inst
              ? `${fmtUsd(position, 0)} position · ${fmtUsd(newMargin)} margin (1:${f.leverage}) · ${fmtUsd(f.transfer ? 0 : openFeeFor(position, inst.feeRate, inst.feeFlat))} fee`
              : inst && !q
                ? 'Waiting for the live price…'
                : 'Enter the amount and leverage.'}
          </span>
        </div>
        {busy && <Spinner />}
        <button type="button" className={cx(btn, 'h-10 border border-line-strong')} onClick={onClose}>
          Cancel
        </button>
        {(['short', 'long'] as const).map((side) => (
          <button
            key={side}
            type="button"
            className={cx(
              'grid h-[58px] w-44 place-items-center rounded-lg text-sm leading-tight font-semibold disabled:cursor-not-allowed disabled:opacity-45',
              side === 'long' ? 'bg-btn-long text-btn-long-fg' : 'bg-btn-short text-btn-short-fg',
            )}
            disabled={!ready || !q || !!checks[side]}
            title={checks[side] ? `${sideLabel(side)} can't open: ${checks[side]!.title}` : undefined}
            onMouseEnter={() => setEstSide(side)}
            onFocus={() => setEstSide(side)}
            onClick={() => void place(side)}
          >
            <span>
              {sideName(side)}
              <br />
              <span className="num text-xs font-normal">at {at(side)}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Modal open onClose={onClose} title={`Open a position for account ${s?.label ?? ''}`} width={1100} footer={footer}>
      <AccountStrip s={s} after={newMargin ? { margin: newMargin.toString(), label: 'after this trade' } : undefined} />
      <Section title="Position" />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
      <div className="grid max-w-md content-start gap-2">
        <Row label="Instrument">
          <InstrumentPicker value={inst?.symbol ?? ''} onChange={(sym) => set('symbol', sym)} />
        </Row>
        <Row label="Amount (lots)">
          <Affixed suffix={units && inst ? `= ${fmtQty(units)} ${inst.unit}${position ? ` · ${fmtUsd(position, 0)} position` : ''}` : undefined}>
            <input className={cx(input, 'num', bad('size') && 'border-warn!')} value={f.lots} onChange={(e) => set('lots', e.target.value)} aria-label="Amount in lots" />
          </Affixed>
        </Row>
        <Row label="Leverage">
          {levRange ? (
            <LeverageField value={f.leverage} onChange={(v) => set('leverage', v)} range={levRange} defaultLev={inst?.defaultLeverage} margin={{ before: null, after: newMargin }} />
          ) : (
            <Affixed prefix="1:">
              <input className={cx(input, 'num', bad('leverage') && 'border-warn!')} value={f.leverage} onChange={(e) => set('leverage', e.target.value)} aria-label="Leverage" />
            </Affixed>
          )}
        </Row>
        <Row label={<span />}>
          <label className="flex items-center gap-2 text-[12px] text-fg">
            <input type="radio" name="desk-rate" checked={!specific && !chart} onChange={() => set('mode', 'current')} />
            Current rate
          </label>
        </Row>
        <Row
          label={
            <label className="flex items-center gap-2 text-[12px] text-fg">
              <input type="radio" name="desk-rate" checked={specific} disabled={f.transfer} onChange={() => set('mode', 'specific')} />
              Specific rate
            </label>
          }
        >
          <input className={cx(input, 'num', specific && bad('price') && 'border-warn!')} value={f.price} disabled={!specific} onChange={(e) => set('price', e.target.value)} aria-label="Specific rate" />
        </Row>
        {ctx.canBackdate && (
          <Row label={<span />}>
            <label className="flex items-center gap-2 text-[12px] text-fg" title={f.transfer ? 'A transferred trade opens at the current rate; use Pick on chart in Edit afterwards' : undefined}>
              <input type="radio" name="desk-rate" checked={chart} disabled={f.transfer} onChange={() => set('mode', 'chart')} />
              <b>From chart</b>
              <span className="text-[11px] text-muted">a past price and time</span>
            </label>
          </Row>
        )}
        {chart && <ChartEntryFields at={f.at} price={f.entry} atMs={atMs} check={check} onAt={(v) => (setF((x) => ({ ...x, at: v, span: 1 })), setRefused(null))} onPrice={(v) => set('entry', v)} />}
        <Toggled label="Stop loss" on={f.slOn} value={f.sl} bad={bad('sl')} onOn={(v) => set('slOn', v)} onValue={(v) => set('sl', v)} />
        <Toggled label="Take profit" on={f.tpOn} value={f.tp} bad={bad('tp')} onOn={(v) => set('tpOn', v)} onValue={(v) => set('tp', v)} />
        {ctx.canTransfer && (
          <Row label="Transfer">
            <label className="flex items-start gap-2 text-[12px] text-fg">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={f.transfer}
                onChange={(e) => {
                  set('transfer', e.target.checked);
                  if (e.target.checked) set('mode', 'current');
                }}
                disabled={chart}
              />
              <span>
                <b>Transferred trade</b>
                <span className="block text-[11px] text-muted">From the client’s previous broker. No open fee, not copied to followers, not counted for rewards. Set its real dates and prices in Edit afterwards.</span>
              </span>
            </label>
          </Row>
        )}
      </div>
      {!inst && <div className="grid h-[240px] place-items-center rounded-lg border border-line text-[12px] text-muted">Choose an instrument to see its chart and estimate.</div>}
      {inst && (
        <div className="flex min-w-0 flex-col gap-2">
          <PlainWords inst={inst} side={estSide} lots={f.lots} leverage={f.leverage} entry={entry ?? (specific && PRICE.test(f.price) ? f.price : null)} quote={q} free={s?.freeMargin ?? null} stopOut={stopOut} />
          <DeskChart symbol={inst.symbol} dp={inst.priceDecimals} quote={q} lines={lines} marks={marks} onDrag={dragged} height={chart ? 300 : 240} pick={chart ? { at: atMs, onPick: picked } : undefined} accountId={ctx.accountId} followers={ctx.master} />
          <div role="radiogroup" aria-label="Estimate for" className="flex items-center gap-1 text-[11px] text-muted">
            Estimate for
            {(['long', 'short'] as const).map((sd) => (
              <button key={sd} type="button" role="radio" aria-checked={estSide === sd} onClick={() => setEstSide(sd)} className={cx('h-6 rounded-md border px-2 font-semibold', estSide === sd ? (sd === 'long' ? 'border-up text-up' : 'border-down text-down') : 'border-line')}>
                {sideLabel(sd)}
              </button>
            ))}
          </div>
          <OpenEstimate
            inst={inst}
            side={estSide}
            lots={f.lots}
            leverage={f.leverage}
            quote={q}
            tp={tp}
            sl={sl}
            account={s && s.marginUsed !== undefined && s.stopOut ? { accountId: ctx.accountId, equity: s.equity, marginUsed: s.marginUsed, stopOut: s.stopOut } : null}
            onStopOut={setStopOut}
            entry={entry}
          />
          {units && q && (
            <OpenRateImpact
              inst={inst}
              side={estSide}
              perPoint={D.from(units).mul(usdPerPoint(inst.contractSize, q.fx))}
              exit={D.from(estSide === 'long' ? q.bid : q.ask)}
              exitLabel="at today's close rate"
              original={D.from(estSide === 'long' ? q.ask : q.bid)}
              originalLabel="Live rate"
              typed={entry ? D.from(entry) : specific && PRICE.test(f.price) ? D.from(f.price) : null}
              master={ctx.master}
            />
          )}
        </div>
      )}
      </div>
    </Modal>
  );
}

/** Admin pages a refusal's links open, in a new tab so the pop-up stays. */
const ADMIN_LINK: Partial<Record<DeskFixLink, string>> = {
  instruments: '/admin?tab=instruments',
  kyc: '/admin?tab=kyc',
  master: '/admin?tab=masters',
};

/**
 * Why the trade can't open, with the figures, and what the dealer can do (DEALER_OPEN_REFUSALS_PLAN.md):
 * one-click fixes for the form, links to where the rest is changed. `server`: the API refused the click.
 */
function RefusalNote({ refusal: r, side, server, crmId, details, onFix }: { refusal: DeskRefusal; side: 'long' | 'short' | null; server: boolean; crmId: string | null; details: string; onFix: (x: DeskFix) => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const copy = (key: string, text: string) => {
    void navigator.clipboard?.writeText(text).then(() => setCopied(key));
  };
  const fixBtn = 'h-[30px] rounded-lg px-3.5 text-xs font-semibold';
  return (
    <div role="alert" className="grid grid-cols-[18px_minmax(0,1fr)] gap-x-2.5 gap-y-2 rounded-xl bg-warn-bg px-3.5 py-3 text-[12.5px]">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="mt-px text-warn">
        <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <path d="M12 9v4M12 17h.01" />
      </svg>
      <div className="flex min-w-0 flex-col gap-1">
        <b className="text-sm font-semibold">
          {side ? `${sideLabel(side)} can't open: ` : "Can't open: "}
          {r.title}
        </b>
        <span>{r.text}</span>
        {server && r.fixes[0]?.kind !== 'copy' && <span className="text-[11.5px] text-muted">Sent and refused: nothing was opened.</span>}
      </div>
      {(r.fixes.length > 0 || r.note) && (
        <>
          <span />
          <div className="flex flex-wrap items-center gap-2">
            {r.fixes.length > 0 && <span className="text-[10.5px] font-semibold tracking-wide text-muted uppercase">What you can do</span>}
            {r.fixes.map((x, i) => {
              const tone = i === 0 && x.kind !== 'link' ? 'bg-fg text-bg' : 'border border-line-strong bg-panel text-fg';
              const after = x.kind === 'link' || x.kind === 'copy' ? x.note : undefined;
              let control: ReactNode;
              if (x.kind === 'link' && ADMIN_LINK[x.to]) {
                control = (
                  <a href={ADMIN_LINK[x.to]} target="_blank" rel="noopener" className={cx(fixBtn, tone, 'inline-flex items-center no-underline')}>
                    {x.label} ↗
                  </a>
                );
              } else if (x.kind === 'link' && x.to === 'client') {
                control = crmId ? (
                  <button type="button" className={cx(fixBtn, tone)} onClick={() => copy('crm', crmId)}>
                    {copied === 'crm' ? 'CRM ID copied' : `Copy CRM ID ${crmId}`}
                  </button>
                ) : (
                  <span className="text-[11.5px]">{x.label}</span>
                );
              } else if (x.kind === 'copy') {
                control = (
                  <button type="button" className={cx(fixBtn, tone)} onClick={() => copy('details', details)}>
                    {copied === 'details' ? 'Copied' : x.label}
                  </button>
                );
              } else if (x.kind === 'notional') {
                return null;
              } else {
                control = (
                  <button type="button" className={cx(fixBtn, tone)} onClick={() => onFix(x)}>
                    {x.label}
                  </button>
                );
              }
              return (
                <span key={`${x.kind}-${i}`} className="contents">
                  {control}
                  {after && <span className="text-[11.5px] text-muted">{after}</span>}
                </span>
              );
            })}
            {r.note && <span className="text-[11.5px] text-muted">{r.note}</span>}
          </div>
        </>
      )}
    </div>
  );
}

/** Why a follower's copy would not open, in the dealer's words. */
const COPY_NOTE: Record<string, string> = {
  below_min: 'Too small: under the minimum',
  insufficient_funds: 'Not enough free margin in the copy',
  market_closed: 'Market closed',
  paused: 'Follower paused',
  master_paused: 'Master paused: opens are not copied',
  copy_restricted: 'Copying is restricted for this client',
  trading_restricted: 'Trading is restricted for this client',
  rejected: 'Not accepted',
};

/**
 * A master account: the copy each follower would open, before the click (MASTER_ORDER_SIZE_PLAN.md).
 * From chart (`at`), each copy opens at the entry too, and shows its profit or loss since then.
 */
function MasterCopies({
  accountId,
  symbol,
  side,
  lots,
  leverage,
  price,
  at,
}: {
  accountId: string;
  symbol: string;
  side: 'long' | 'short';
  lots: string;
  leverage: string;
  price: string | null;
  /** From chart: the entry's minute (ISO). */
  at?: string | null;
}) {
  const [data, setData] = useState<MasterCopyPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      api
        .post<MasterCopyPreview>(`/api/admin/desk/accounts/${accountId}/orders/copies`, { symbol, side, lots, leverage: Number(leverage), price: price ?? undefined, at: at ?? undefined })
        .then((r) => {
          if (!live) return;
          setData(r);
          setError(null);
        })
        .catch((e) => live && setError(err(e)));
    }, 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [accountId, symbol, side, lots, leverage, price, at]);
  const shell = 'flex flex-col gap-2 rounded-xl bg-raised/60 px-3.5 py-2.5 text-[12.5px]';
  if (error) return <div className={cx(shell, 'text-muted')}>Copies: {error}</div>;
  if (!data) return <div className={cx(shell, 'text-muted')}>Working out each follower’s copy…</div>;
  if (!data.followers) return <div className={cx(shell, 'text-muted')}>No followers yet: nothing is copied.</div>;
  const th = 'py-1 pr-3 text-left font-normal text-muted';
  const td = 'num py-1 pr-3';
  return (
    <div className={shell}>
      <div className="flex flex-wrap items-center gap-2">
        <b className="font-semibold">
          Copies to {data.followers} follower{data.followers === 1 ? '' : 's'} · about {fmtUsd(data.total, 0)} in total
          {data.sinceEntry != null && (
            <>
              {' '}
              · <span className={tone(data.sinceEntry)}>{signed(data.sinceEntry)}</span> since the entry
            </>
          )}
        </b>
        {data.capped > 0 && (
          <span className="rounded-full bg-warn-bg px-2 py-0.5 text-[11px] font-semibold">
            {data.capped} capped at {symbol}'s max order
          </span>
        )}
        {data.skipped > 0 && <span className="rounded-full bg-raised px-2 py-0.5 text-[11px] font-semibold">{data.skipped} not copied</span>}
        <button type="button" aria-expanded={open} onClick={() => setOpen(!open)} className="ml-auto h-7 rounded-lg border border-line-strong bg-panel px-3 text-[11.5px] font-semibold">
          Per follower {open ? '▴' : '▾'}
        </button>
      </div>
      {open && (
        <div className="max-h-56 overflow-auto">
          <table className="w-full min-w-[560px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-line">
                <th className={th}>Client</th>
                <th className={cx(th, 'text-right')}>Copy equity</th>
                <th className={cx(th, 'text-right')}>×</th>
                <th className={cx(th, 'text-right')}>Their share</th>
                <th className={cx(th, 'text-right')}>Copy</th>
                {data.sinceEntry != null && <th className={cx(th, 'text-right')}>Since entry</th>}
                <th className={th}>Note</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.copyId} className="border-b border-line last:border-0">
                  <td className="py-1 pr-3">{r.user.name ?? r.user.email}</td>
                  <td className={cx(td, 'text-right')}>{fmtUsd(r.copyEquity, 0)}</td>
                  <td className={cx(td, 'text-right')}>{fmt(r.multiplier, D.from(r.multiplier).round(0).eq(D.from(r.multiplier)) ? 0 : 2)}</td>
                  <td className={cx(td, 'text-right')}>{fmtUsd(r.share, 0)}</td>
                  <td className={cx(td, 'text-right font-semibold')}>{r.notional ? fmtUsd(r.notional, 0) : '—'}</td>
                  {data.sinceEntry != null && <td className={cx(td, 'text-right', tone(r.sinceEntry))}>{signed(r.sinceEntry)}</td>}
                  <td className={cx('py-1', r.cappedFrom ? 'text-warn' : 'text-muted')}>{r.cappedFrom ? `Capped at ${symbol}'s max order` : r.skip ? (COPY_NOTE[r.skip] ?? r.skip) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface EditPreview {
  amount: string;
  changed: string[];
  before: Record<string, string | number | null>;
  after: Record<string, string | number | null>;
  margin: { before: string; after: string; freeBefore: string; freeAfter: string } | null;
}
const FIELD: Record<string, string> = {
  side: 'Type',
  qty: 'Amount',
  leverage: 'Leverage',
  openPrice: 'Open price',
  openedAt: 'Open time',
  closePrice: 'Close price',
  closedAt: 'Close time',
  realizedPnl: 'Result',
  tpPrice: 'Take profit',
  slPrice: 'Stop loss',
};
/** A figure as the pop-up shows it; the amount in lots, like its box, once the instrument's lot size is known. */
const showField = (k: string, v: string | number | null, lotSize?: string) =>
  v == null ? '—' : k === 'side' ? sideLabel(v as 'long' | 'short') : k.endsWith('At') ? fmtDateTime(String(v)) : k === 'realizedPnl' ? signed(String(v)) : k === 'leverage' ? `1:${v}` : k === 'qty' && lotSize ? `${D.from(v).div(lotSize).toString()} lots` : String(v);

/** What Save will do, before the dealer commits: each changed figure, the margin, and the money that moves. */
function WhatSaveDoes({ closed, preview, amount, corrections, editing, tpsl, lotSize, master }: { closed: boolean; preview: EditPreview | null; amount: string | null; corrections: Correction[]; editing: boolean; tpsl: boolean; lotSize?: string; master: boolean }) {
  const lines: ReactNode[] = [];
  if (editing && preview) {
    for (const k of preview.changed.filter((k) => k !== 'realizedPnl')) {
      lines.push(
        <span key={k}>
          {FIELD[k] ?? k} <s className="text-muted">{showField(k, preview.before[k] ?? null, lotSize)}</s> → <b>{showField(k, preview.after[k] ?? null, lotSize)}</b>
        </span>,
      );
    }
    if (preview.margin && preview.margin.before !== preview.margin.after) {
      lines.push(
        <span key="margin">
          Margin <s className="text-muted">{fmtUsd(preview.margin.before)}</s> → <b>{fmtUsd(preview.margin.after)}</b>, free margin {fmtUsd(preview.margin.freeBefore)} → <b className={D.from(preview.margin.freeAfter).isNeg() ? 'text-down' : ''}>{fmtUsd(preview.margin.freeAfter)}</b>.
        </span>,
      );
    }
    if (closed && preview.changed.includes('realizedPnl')) {
      lines.push(
        <span key="result">
          Result <s className="text-muted">{showField('realizedPnl', preview.before.realizedPnl ?? null)}</s> → <b>{showField('realizedPnl', preview.after.realizedPnl ?? null)}</b>.
        </span>,
      );
    }
  }
  for (const c of corrections) lines.push(<span key={c.label}>{c.label} corrected; the difference is posted to the balance.</span>);
  if (tpsl) lines.push(<span key="tpsl">Stop loss / take profit set on the trade.</span>);
  const money = amount == null ? null : D.from(amount);
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border border-line bg-bg px-3 py-2 text-[12.5px]">
      <span className="text-[10.5px] font-semibold uppercase tracking-wide text-muted">What Save will do</span>
      {lines.length ? lines : <span className="text-muted">Working it out…</span>}
      {money && !money.isZero() ? (
        <span className={money.isNeg() ? 'text-down' : 'text-up'}>
          <b>{signed(money.toString())}</b> {master ? 'posted to the master’s balance now; its followers’ copies keep theirs.' : 'posted to the balance now, on the client’s statement.'}
        </span>
      ) : editing && !closed ? (
        <span className="text-muted">No money moves now: profit runs from the new figures and is paid when the trade closes.</span>
      ) : null}
    </div>
  );
}

/** A UTC 'YYYY-MM-DDTHH:mm' for a datetime-local box, and back to ISO. */
const toBox = (iso: string | null | undefined) => (iso ? iso.slice(0, 16) : '');
const fromBox = (v: string) => (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v) ? `${v}:00Z` : '');

// ---------------------------------------------------------------- "From chart" (CHART_ENTRY_PLAN.md)

interface EntryCheck {
  at: string;
  price: string;
  priceCheck: { status: 'ok' | 'outside' | 'no_data'; tf: string | null; low: string | null; high: string | null; offByPct: string | null };
  tradedAt: string | null;
  slHitAt: string | null;
  tpHitAt: string | null;
  nights: number;
  swapEstimate: string | null;
}
type EntryState = { data: EntryCheck | null; error: string | null; loading: boolean };

const SHORT_FMT = new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
/** "22 Sep, 09:14" (UTC), for the Sell / Buy buttons. */
const shortWhen = (ms: number) => SHORT_FMT.format(ms);

/** The server's checks of a chart entry, refreshed as the dealer picks or types: our candle, TP / SL hits since, the swap it would have paid. */
function useEntryCheck(a: { accountId: string; symbol: string; side: 'long' | 'short'; lots: string; at: string; price: string | null; span: number; tp: string; sl: string } | null): EntryState {
  const [st, setSt] = useState<EntryState>({ data: null, error: null, loading: false });
  const key = a && a.at && a.price ? JSON.stringify(a) : '';
  useEffect(() => {
    if (!a || !key) return setSt({ data: null, error: null, loading: false });
    setSt((x) => ({ ...x, loading: true }));
    let gone = false;
    const t = window.setTimeout(() => {
      api
        .post<EntryCheck>(`/api/admin/desk/accounts/${a.accountId}/orders/preview`, {
          symbol: a.symbol,
          side: a.side,
          lots: a.lots,
          span: a.span,
          tpPrice: a.tp.trim() || null,
          slPrice: a.sl.trim() || null,
          entry: { price: a.price, at: a.at },
        })
        .then((data) => !gone && setSt({ data, error: null, loading: false }))
        .catch((e) => !gone && setSt({ data: null, error: err(e), loading: false }));
    }, 350);
    return () => {
      gone = true;
      window.clearTimeout(t);
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return st;
}

const Chip = ({ tone: t, children }: { tone: 'ok' | 'warn' | 'muted'; children: ReactNode }) => (
  <span className={cx('inline-flex items-center gap-1 self-start rounded-full px-2 py-0.5 text-[11px] font-semibold', t === 'ok' ? 'bg-up/10 text-up' : t === 'warn' ? 'bg-warn-bg text-warn' : 'bg-raised text-muted')}>{children}</span>
);

/** What the dealer picked or typed for the entry, and the checks on it. */
function ChartEntryFields({ at, price, atMs, check, onAt, onPrice }: { at: string; price: string; atMs: number | null; check: EntryState; onAt: (v: string) => void; onPrice: (v: string) => void }) {
  const c = check.data;
  const pc = c?.priceCheck;
  const days = atMs ? Math.floor((Date.now() - atMs) / 86_400_000) : null;
  return (
    <div className="ml-4 flex flex-col gap-2 rounded-lg border border-dashed border-accent/70 p-2.5">
      <Row label="Open time (UTC)">
        <Affixed suffix={atMs ? `${utcLabel(atMs)} · ${londonLabel(atMs)} London` : 'Click a candle on the chart, or type the time'}>
          <input type="datetime-local" step={60} className={cx(input, 'num')} value={at} max={toBox(new Date().toISOString())} onChange={(e) => onAt(e.target.value)} aria-label="Entry time (UTC)" />
        </Affixed>
      </Row>
      <Row label="Open price">
        <input className={cx(input, 'num')} value={price} onChange={(e) => onPrice(e.target.value)} aria-label="Entry price" placeholder="Click a candle" />
      </Row>
      {check.loading && !c && <Spinner />}
      {check.error && <p className="m-0 text-[12px] text-down">{check.error}</p>}
      {c && pc && (
        <div className="flex flex-col gap-1">
          <CandleChips at={c.at} check={pc} tradedAt={c.tradedAt} onUse={onAt} />
          {c.slHitAt && <Chip tone="warn">! The price reached this stop loss on {utcLabel(Date.parse(c.slHitAt))}</Chip>}
          {c.tpHitAt && <Chip tone="warn">! The price reached this take profit on {utcLabel(Date.parse(c.tpHitAt))}</Chip>}
          <span className="text-[11px] text-muted">
            {days === 0 ? 'Today.' : `${days} day${days === 1 ? '' : 's'} ago.`} The trade runs from this price and time; nothing between then and now is replayed.
            {c.nights > 0 && ` No swap is charged for the ${c.nights} night${c.nights === 1 ? '' : 's'} since${c.swapEstimate ? ` (it would be ${signed(c.swapEstimate)})` : ''}; add it as a rollover adjustment if needed.`}
          </span>
        </div>
      )}
    </div>
  );
}

/** Our candle at the picked minute: inside, outside (with the minute in a longer candle that traded at the price), or none. */
function CandleChips({ at, check: pc, tradedAt, onUse }: { at: string; check: EntryCheck['priceCheck']; tradedAt: string | null; onUse: (box: string) => void }) {
  if (pc.status === 'ok') {
    return (
      <Chip tone="ok">
        ✓ Inside our {pc.tf} candle {pc.low} – {pc.high}
      </Chip>
    );
  }
  if (pc.status === 'outside' && tradedAt) {
    return (
      <span className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted">
        <Chip tone="warn">
          ! Not traded at {toBox(at).slice(11)}; first traded at {toBox(tradedAt).slice(11)} in that candle
        </Chip>
        <button type="button" className="h-6 rounded-md border border-accent px-2 font-semibold text-accent" onClick={() => onUse(toBox(tradedAt))}>
          Use {toBox(tradedAt).slice(11)}
        </button>
      </span>
    );
  }
  if (pc.status === 'outside') {
    return (
      <Chip tone="warn">
        ! {pc.offByPct}% outside our {pc.tf} candle {pc.low} – {pc.high}
      </Chip>
    );
  }
  return <Chip tone="muted">No candle of ours at that minute (market closed?)</Chip>;
}

/** A UTC date and minute box, with the moment in words and London time (BST or GMT as it was) under it. */
function TimeBox({ value, onChange, label, min }: { value: string; onChange: (v: string) => void; label: string; min?: string }) {
  const iso = fromBox(value);
  const ms = iso ? Date.parse(iso) : null;
  return (
    <Affixed suffix={ms ? `${utcLabel(ms)} · ${londonLabel(ms)} London` : 'Click a candle on the chart, or type the time'}>
      <input type="datetime-local" step={60} className={cx(input, 'num')} value={value} min={min} max={toBox(new Date().toISOString())} onChange={(e) => onChange(e.target.value)} aria-label={label} />
    </Affixed>
  );
}

/** desk/chart-entry.ts previewPoint */
interface PointCheck {
  field: 'open' | 'close';
  at: string;
  price: string;
  priceCheck: EntryCheck['priceCheck'];
  tradedAt: string | null;
  slHitAt: string | null;
  tpHitAt: string | null;
  nights: number;
  swapAfter: string | null;
}

/**
 * The Open pop-up's checks for a moment of an existing trade, picked on the chart or typed: a new open
 * or close in Edit, or a close from chart. `tp` / `sl`: the ones the form holds (the trade's own if left out).
 */
function PointChecks({
  positionId,
  field,
  label,
  price,
  at,
  span,
  tp,
  sl,
  openedAt,
  onUseAt,
}: {
  positionId: string;
  field: 'open' | 'close';
  label: string;
  price: string;
  at: string;
  span: number;
  tp?: string | null;
  sl?: string | null;
  /** The trade's open (as typed), for what was reached before a close. */
  openedAt?: string;
  onUseAt: (box: string) => void;
}) {
  const [st, setSt] = useState<{ data: PointCheck | null; error: string | null }>({ data: null, error: null });
  const key = JSON.stringify([positionId, field, price, at, span, tp, sl, openedAt]);
  useEffect(() => {
    let gone = false;
    const t = window.setTimeout(() => {
      api
        .post<PointCheck>(`/api/admin/desk/positions/${positionId}/point/preview`, { field, price, at, span, tpPrice: tp, slPrice: sl, openedAt })
        .then((data) => !gone && setSt({ data, error: null }))
        .catch((e) => !gone && setSt({ data: null, error: err(e) }));
    }, 350);
    return () => {
      gone = true;
      window.clearTimeout(t);
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (st.error) return <p className="m-0 text-[12px] text-down">{label}: {st.error}</p>;
  const c = st.data;
  if (!c) return null;
  const when = (x: string) => utcLabel(Date.parse(x));
  const hit = (what: string, x: string) =>
    field === 'open' ? `! The price reached this ${what} on ${when(x)}, after this open: the trade would have closed then` : `! The price reached the trade’s ${what} on ${when(x)}, before this close`;
  return (
    <div className="flex flex-col gap-1" data-testid={`point-checks-${field}`}>
      <span className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span className="font-semibold text-muted">
          {label} {toBox(c.at).slice(11)} UTC at {c.price}:
        </span>
        <CandleChips at={c.at} check={c.priceCheck} tradedAt={c.tradedAt} onUse={onUseAt} />
      </span>
      {c.slHitAt && <Chip tone="warn">{hit('stop loss', c.slHitAt)}</Chip>}
      {c.tpHitAt && <Chip tone="warn">{hit('take profit', c.tpHitAt)}</Chip>}
      {c.nights > 0 && (
        <span className="text-[11px] text-muted">
          The {c.nights} night{c.nights === 1 ? '' : 's'} after this close were charged swap{c.swapAfter ? ` (about ${signed(c.swapAfter)} at today’s price)` : ''}; it stays on the trade. Add a rollover adjustment if the case needs it.
        </span>
      )}
    </div>
  );
}

/** copy/masters.ts positionFollowers: one row per signal and follower. */
interface FollowerCopy {
  action: string;
  status: string;
  positionStatus: string | null;
  positionSide: 'long' | 'short' | null;
  positionQty: string | null;
  user: { name: string | null; email: string };
}

/** A master trade's copies on its followers' accounts: what an edit of the master's trade leaves as it is. */
function useCopies(masterId: string | null, positionId: string): FollowerCopy[] | null {
  const [rows, setRows] = useState<FollowerCopy[] | null>(null);
  useEffect(() => {
    if (!masterId) return;
    let gone = false;
    api
      .get<{ followers: FollowerCopy[] }>(`/api/admin/masters/${masterId}/positions/${positionId}/followers`)
      .then((r) => !gone && setRows(r.followers.filter((x) => x.action === 'open' && x.status === 'done' && x.positionSide && x.positionQty)))
      .catch(() => !gone && setRows([]));
    return () => {
      gone = true;
    };
  }, [masterId, positionId]);
  return rows;
}

/** On a master's trade: the followers' copies, which an edit leaves as they are (MASTER_EDITS_PLAN.md). */
function CopiesNotice({ copies, inst, closed, turned }: { copies: FollowerCopy[]; inst: ReturnType<typeof useInstrument>; closed: boolean; turned: boolean }) {
  const held = closed ? copies : copies.filter((c) => c.positionStatus === 'open');
  if (!held.length) return <p className="m-0 text-[11.5px] text-muted">No follower {closed ? 'had' : 'holds'} a copy of this trade. An edit changes the master’s trade only.</p>;
  const lots = (c: FollowerCopy) => (inst ? D.from(c.positionQty!).div(inst.lotSize).toString() : c.positionQty);
  const n = held.length;
  return (
    <div className={cx('flex gap-2.5 rounded-lg border px-3 py-2 text-[12.5px]', turned ? 'border-warn/50 bg-warn-bg' : 'border-line bg-raised/40')} data-testid="copies-notice">
      <span className={cx('mt-0.5 grid h-[18px] w-[18px] flex-none place-items-center rounded-full border text-[11px] font-bold', turned ? 'border-warn text-warn' : 'border-muted text-muted')} aria-hidden="true">
        i
      </span>
      <div className="flex min-w-0 flex-col gap-0.5">
        <b className="font-semibold">
          {n} follower{n === 1 ? '' : 's'} {closed ? 'had' : n === 1 ? 'holds' : 'hold'} a copy of this trade.
        </b>
        <span>
          {closed
            ? 'Save and Delete change the master’s trade only. Their copies keep their own figures, fees, swap and results.'
            : 'Save changes the master’s trade only. Their copies keep their own type, amount and leverage, and a later close still reaches them as the same share (close half of the master, and half of each copy closes).'}
        </span>
        {turned && <span className="text-warn">The type changes: TP and SL changes on this trade won’t reach copies that face the other way.</span>}
        <span className="num text-[11.5px] text-muted">
          {held
            .slice(0, 8)
            .map((c) => `${c.user.name ?? c.user.email}: ${sideLabel(c.positionSide!)} ${lots(c)} lots`)
            .join(' · ')}
          {n > 8 ? ` · and ${n - 8} more` : ''}
        </span>
      </div>
    </div>
  );
}

/** desk/trade-restore.ts CopyRestore: a follower's copy in the master's restore. */
interface CopyRestore {
  copyId: string;
  positionId: string;
  user: { id: string; name: string | null; email: string };
  side: 'long' | 'short';
  qty: string;
  amount: string;
  problem: { code: string; message: string } | null;
}

/**
 * On a master's trade: the followers' copies its close reached, each restored with it (its close taken
 * back on the follower's managed account) or why it stays closed; or, unticked, the master's trade only.
 */
function RestoreCopies({ copies, amount, on, onOn, inst }: { copies: CopyRestore[]; amount: string; on: boolean; onOn: (v: boolean) => void; inst: ReturnType<typeof useInstrument> }) {
  if (!copies.length) return <p className="m-0 text-[11.5px] text-muted">No follower’s copy closed with this trade: Restore opens the master’s trade only.</p>;
  const lots = (c: CopyRestore) => (inst ? D.from(c.qty).div(inst.lotSize).toString() : c.qty);
  const ok = copies.filter((c) => !c.problem);
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line bg-raised/40 px-3 py-2 text-[12.5px]" data-testid="restore-copies">
      <label className="flex items-start gap-2">
        <input type="checkbox" checked={on} onChange={(e) => onOn(e.target.checked)} className="mt-0.5" />
        <span>
          <b className="font-semibold">Restore the followers’ copies too</b>
          <span className="block text-[11.5px] text-muted">
            {on
              ? `${ok.length} of ${copies.length} cop${copies.length === 1 ? 'y opens' : 'ies open'} again as it was, its close taken back on the follower’s managed account (${signed(amount)} together); each follower is told. They follow this trade again.`
              : 'The master’s trade only: their copies stay closed with their results, and closing this trade again later won’t reach them.'}
          </span>
        </span>
      </label>
      {on && (
        <div className="max-h-48 overflow-auto">
          <table className="w-full min-w-[520px] border-collapse text-[12px]">
            <thead>
              <tr className="border-b border-line text-left text-muted">
                <th className="py-1 pr-3 font-normal">Follower</th>
                <th className="py-1 pr-3 font-normal">Copy</th>
                <th className="py-1 pr-3 text-right font-normal">Balance change</th>
                <th className="py-1 font-normal" />
              </tr>
            </thead>
            <tbody>
              {copies.map((c) => (
                <tr key={c.positionId} className="border-b border-line last:border-0">
                  <td className="py-1 pr-3">{c.user.name ?? c.user.email}</td>
                  <td className="num py-1 pr-3">
                    <span className={c.side === 'long' ? 'text-up' : 'text-down'}>{sideLabel(c.side)}</span> {lots(c)} lots
                  </td>
                  <td className={cx('num py-1 pr-3 text-right', tone(c.amount))}>{signed(c.amount)}</td>
                  <td className={cx('py-1', c.problem ? 'text-warn' : 'text-up')}>{c.problem ? `Stays closed: ${c.problem.message}` : 'Restored with it'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** One of the trade's figures in a small box (Restore trade, Close position). */
const kv = (label: string, value: ReactNode) => (
  <div className="flex min-w-0 flex-col gap-px rounded-lg border border-line bg-bg px-3 py-2">
    <span className="text-[11px] text-muted">{label}</span>
    <span className="num truncate text-[13px]">{value}</span>
  </div>
);

/** desk/trade-restore.ts previewRestore */
interface RestorePreview {
  tradeId: number | null;
  openPrice: string;
  openedAt: string;
  closePrice: string | null;
  closedAt: string | null;
  closeReason: string | null;
  qty: string;
  leverage: number;
  tpPrice: string | null;
  slPrice: string | null;
  amount: string;
  realizedTakenBack: string;
  closeFeeRefunded: string;
  swapOnTrade: string;
  balanceBefore: string;
  balanceAfter: string;
  livePrice: string | null;
  openPnlNow: string | null;
  margin: string;
  freeMarginAfter: string | null;
  slHitAt: string | null;
  tpHitAt: string | null;
  nightsClosed: number;
  swapEstimate: string | null;
  ok: boolean;
  problems: { code: string; message: string }[];
  /** A master's trade: its followers' copies. */
  copies: CopyRestore[];
  copiesAmount: string;
}

/**
 * Restore a closed trade (CLIENT_ONLINE_RESTORE_PLAN.md part B): it opens again as it was, with its
 * open time, price, amount, leverage and (when kept) TP / SL; the close is taken back on the balance.
 * The reason is optional. A role set to "request" sends it for approval. A master's trade restores its
 * followers' copies with it unless unticked.
 */
export function RestoreDialog({ pos, onClose }: { pos: Pos; onClose: () => void }) {
  const ctx = useDesk();
  const inst = useInstrument(pos.symbol);
  const quote = useQuote(pos.symbol);
  const [keep, setKeep] = useState(true);
  // A master's trade: its followers' copies are restored with it unless unticked.
  const [withCopies, setWithCopies] = useState(true);
  const [reason, setReason] = useState('');
  const [pv, setPv] = useState<RestorePreview | null>(null);
  const [fail, setFail] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let gone = false;
    const load = () =>
      api
        .post<RestorePreview>(`/api/admin/desk/positions/${pos.id}/restore/preview`, { keepTpSl: keep, copies: withCopies })
        .then((r) => !gone && (setPv(r), setFail(null)))
        .catch((e) => !gone && setFail(err(e)));
    void load();
    // The live price moves the open P&L and the margin check.
    const t = setInterval(load, 5000);
    return () => {
      gone = true;
      clearInterval(t);
    };
  }, [pos.id, keep, withCopies]);
  const save = async () => {
    setBusy(true);
    setFail(null);
    try {
      const r = await api.post<Record<string, any>>(`/api/admin/desk/positions/${pos.id}/restore`, { keepTpSl: keep, reason: reason.trim(), copies: withCopies });
      // Done: the pop-up closes and the page reloads; the corner note says what was done (DEALER_POPUP_CLOSE_PLAN.md).
      // A master's: how many of its followers' copies were restored with it, and each that stayed closed.
      const copies = (r.copies ?? []) as CopyRestore[];
      const left = copies.filter((c) => c.problem);
      ctx?.refresh();
      deskNotice({
        title,
        lines: [
          r.pendingApproval ? `Sent for approval as APR-${r.pendingApproval.number}` : `Trade restored${D.from(r.amount).isZero() ? '' : `: ${signed(r.amount)} on the balance`}`,
          ...(copies.length ? [`${copies.length - left.length} of ${copies.length} followers’ copies restored with it`] : []),
        ],
        fail: left.length ? `Stayed closed: ${left.map((c) => `${c.user.name ?? c.user.email} (${c.problem!.message})`).join('; ')}` : null,
      });
      onClose();
    } catch (e) {
      setFail(err(e));
    } finally {
      setBusy(false);
    }
  };
  const openedMs = Date.parse(pos.openedAt);
  const closedMs = pos.closedAt ? Date.parse(pos.closedAt) : null;
  const marks: DeskMark[] = useMemo(
    () => [{ t: openedMs, label: 'Opened', tone: 'accent' }, ...(closedMs != null ? [{ t: closedMs, label: 'Closed (taken back)', tone: 'muted' as const, below: true }] : [])],
    [openedMs, closedMs],
  );
  const lines: DeskLine[] = [
    { id: 'entry', price: pos.entryPrice, label: 'Entry', tone: 'accent' },
    ...(keep ? [{ id: 'tp', price: pos.tpPrice, label: 'TP', tone: 'up' as const, dashed: true }, { id: 'sl', price: pos.slPrice, label: 'SL', tone: 'down' as const, dashed: true }] : []),
  ];
  const lots = inst ? D.from(pos.qty).div(inst.lotSize).toString() : pos.qty;
  const money = (label: string, value: ReactNode, cls = '') => (
    <div className="flex justify-between gap-3 border-b border-line px-3 py-1.5 text-[12.5px] last:border-b-0">
      <span className="text-muted">{label}</span>
      <span className={cx('num', cls)}>{value}</span>
    </div>
  );
  const title = `Restore trade ${fmtTradeId(pos.tradeId)} · ${sideLabel(pos.side)} ${lots} lots ${pos.symbol}`;
  const heading = (
    <>
      Restore trade {fmtTradeId(pos.tradeId)} · {sideLabel(pos.side)} {lots} lots <InstrumentLabel symbol={pos.symbol} size={20} />
    </>
  );
  return (
    <Modal open onClose={onClose} title={title} heading={heading} width={inst ? 900 : 640}>
      <div className="flex flex-col gap-3">
        <p className="m-0 text-[12.5px] text-muted">
          Opens the trade again as it was before it was closed. The close is taken back: its result leaves the {ctx?.master ? 'master’s ' : ''}balance and its close fee is refunded.
        </p>
        {ctx?.master && pv && <RestoreCopies copies={pv.copies} amount={pv.copiesAmount} on={withCopies} onOn={setWithCopies} inst={inst} />}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {kv('Opened', `${fmtDateTime(pos.openedAt)} · ${pos.entryPrice}`)}
          {kv('Lots · leverage', `${lots} · 1:${pos.leverage}`)}
          {kv('TP / SL', `${pos.tpPrice ?? '—'} / ${pos.slPrice ?? '—'}`)}
          {kv('Closed (taken back)', pos.closedAt ? `${fmtDateTime(pos.closedAt)} · ${pos.closePrice ?? '—'}` : '—')}
          {kv('Closed by', pos.closeReason ?? pos.closedBy ?? '—')}
          {kv('Price now', pv?.livePrice ?? '—')}
        </div>
        {inst && (
          <DeskChart
            symbol={pos.symbol}
            dp={inst.priceDecimals}
            quote={quote}
            lines={lines}
            marks={marks}
            defaultTf="1h"
            accountId={ctx?.accountId}
            followers={!!ctx?.master}
            height={220}
          />
        )}
        {pv && (
          <div className="rounded-lg border border-line bg-bg">
            {money('Realized result taken back', signed(D.from(pv.realizedTakenBack).neg().toString()), tone(D.from(pv.realizedTakenBack).neg().toString()))}
            {money('Close fee refunded', signed(pv.closeFeeRefunded), tone(pv.closeFeeRefunded))}
            {money('Swap back on the trade (charged at its close)', signed(pv.swapOnTrade))}
            {money(
              `Nights it was closed: ${pv.nightsClosed}`,
              pv.swapEstimate ? `swap ${signed(pv.swapEstimate)} not charged` : 'no swap',
              'text-muted',
            )}
            {money(`Balance ${fmtUsd(pv.balanceBefore)} → ${fmtUsd(pv.balanceAfter)}`, <b className={tone(pv.amount)}>{signed(pv.amount)}</b>)}
            {money('Open P&L now', pv.openPnlNow ? signed(pv.openPnlNow) : '—', tone(pv.openPnlNow))}
            {money('Free margin after', pv.freeMarginAfter ? fmtUsd(pv.freeMarginAfter) : '—', pv.freeMarginAfter && D.from(pv.freeMarginAfter).isNeg() ? 'text-down' : '')}
          </div>
        )}
        {(pos.tpPrice || pos.slPrice) && (
          <label className="flex items-start gap-2 text-[12.5px]">
            <input type="checkbox" checked={keep} onChange={(e) => setKeep(e.target.checked)} className="mt-0.5" />
            <span>
              Keep {[pos.tpPrice && `TP ${pos.tpPrice}`, pos.slPrice && `SL ${pos.slPrice}`].filter(Boolean).join(' and ')}
              {pv && (pv.slHitAt || pv.tpHitAt) && (
                <span className="block text-warn">
                  While it was closed the market reached its {pv.slHitAt ? `stop loss (${fmtDateTime(pv.slHitAt)})` : ''}
                  {pv.slHitAt && pv.tpHitAt ? ' and ' : ''}
                  {pv.tpHitAt ? `take profit (${fmtDateTime(pv.tpHitAt)})` : ''}.
                </span>
              )}
            </span>
          </label>
        )}
        <label className="flex flex-col gap-1 text-[12px] text-muted">
          Reason (optional)
          <input className={input} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. closed by mistake" />
        </label>
        {pv && pv.problems.length > 0 && (
          <ul className="m-0 flex list-none flex-col gap-1 rounded-lg bg-down/10 p-2.5 text-[12.5px] text-down">
            {pv.problems.map((p) => (
              <li key={p.code}>
                {p.message}
                {p.code === 'tpsl_passed' && keep && (
                  <button type="button" className="ml-2 h-7 rounded-md border border-down/40 px-2.5 text-[11px] font-semibold" onClick={() => setKeep(false)}>
                    Restore without TP / SL
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {fail && <p className="m-0 text-[12.5px] text-down">{fail}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={cx(btn, 'border border-line-strong text-fg')} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={cx(btn, 'bg-accent text-white')} disabled={busy || !pv?.ok} onClick={() => void save()}>
            {busy ? <Spinner /> : ctx?.restoreDirect ? 'Restore trade' : 'Send for approval'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Close… on an open position's row: closes it at the live price, all of it or a part, without going
 * through Edit. A master's close reaches its followers' copies as the same share. Closing at a past
 * price stays in Edit (Close position… → At a past price).
 */
export function ClosePositionDialog({ pos, onClose }: { pos: Pos; onClose: () => void }) {
  const ctx = useDesk();
  const inst = useInstrument(pos.symbol);
  const quote = useQuote(pos.symbol);
  const ticker = useTicker(pos.symbol);
  const copies = useCopies(ctx?.masterId ?? null, pos.id);
  const [busy, setBusy] = useState(false);
  const [fail, setFail] = useState<string | null>(null);
  const lots = inst ? D.from(pos.qty).div(inst.lotSize).toString() : pos.qty;
  // A buy closes at the Bid, a sell at the Ask; the row's figures until the first quote arrives.
  const rate = quote ? (pos.side === 'long' ? quote.bid : quote.ask) : (pos.currentPrice ?? null);
  const profit = inst && quote && rate ? pnl(pos.side, pos.entryPrice, rate, pos.qty, usdPerPoint(inst.contractSize, quote.fx)).toFixed(2) : (pos.unrealizedPnl ?? null);
  const open = ticker ? ticker.open : inst ? isMarketOpen(inst.tradingHours) : true;
  const opensAt = !open && inst ? nextMarketOpen(inst.tradingHours) : null;
  const held = copies?.filter((c) => c.positionStatus === 'open') ?? [];
  const what = `${sideLabel(pos.side)} ${lots} lots ${pos.symbol}`;
  const ref = pos.tradeId != null ? `order ${fmtTradeId(pos.tradeId)}` : pos.id.slice(0, 8);
  const title = `Close position: ${ref} · ${what}`;
  const heading = (
    <>
      Close position: {ref} · <span className={pos.side === 'long' ? 'text-up' : 'text-down'}>{sideLabel(pos.side)}</span> {lots} lots <InstrumentLabel symbol={pos.symbol} size={20} />
    </>
  );
  const close = async (fraction: string) => {
    setBusy(true);
    setFail(null);
    try {
      const r = await api.post<{ closePrice: string | null; realizedPnl: string | null }>(`/api/admin/desk/positions/${pos.id}/close`, { fraction });
      // Done: the pop-up closes and the page reloads; the corner note says what was done (DEALER_POPUP_CLOSE_PLAN.md).
      ctx?.refresh();
      deskNotice({
        title: `${pos.tradeId != null ? `Order ${fmtTradeId(pos.tradeId)}` : `Position ${pos.id.slice(0, 8)}`} · ${what}`,
        lines: [`${fraction === '1' ? 'Position closed' : `${Number(fraction) * 100}% closed`} at ${r.closePrice ?? 'the live price'}${r.realizedPnl != null ? `: ${signed(r.realizedPnl)}` : ''}`],
      });
      onClose();
    } catch (e) {
      setFail(err(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} title={title} heading={heading} width={720}>
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {kv('Opened', `${fmtDateTime(pos.openedAt)} · ${pos.entryPrice}`)}
          {kv('Lots · leverage', `${lots} · 1:${pos.leverage}`)}
          {kv('TP / SL', `${pos.tpPrice ?? '—'} / ${pos.slPrice ?? '—'}`)}
          {kv('Close rate now', rate ?? '—')}
          {kv('P&L now', <span className={tone(profit)}>{signed(profit)}</span>)}
          {kv('Swap', <span className={tone(pos.swap)}>{signed(pos.swap)}</span>)}
        </div>
        <p className="m-0 text-[12.5px] text-muted">
          Closes at the live price. The result goes to the {ctx?.master ? 'master’s' : 'client’s'} balance with the trade’s swap, less the close fee.
        </p>
        {held.length > 0 && (
          <p className="m-0 text-[12.5px] text-muted" data-testid="close-copies-notice">
            {held.length} follower{held.length === 1 ? ' holds' : 's hold'} a copy of this trade. The close reaches them as the same share: close half of the master, and half of each copy closes.
          </p>
        )}
        {!open && (
          <p className="m-0 text-[12.5px] text-down">
            {pos.symbol} market is closed{opensAt ? `: it opens ${fmtDateTime(opensAt)}` : ''}.
          </p>
        )}
        {fail && <p className="m-0 text-[12.5px] text-down">{fail}</p>}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="mr-auto text-[12px] text-muted">Close at the live price:</span>
          {['0.25', '0.5'].map((fr) => (
            <button key={fr} type="button" className={cx(btn, 'border border-line-strong')} disabled={busy || !open} onClick={() => void close(fr)}>
              {Number(fr) * 100}%
            </button>
          ))}
          <button type="button" className={cx(btn, 'bg-btn-short text-white')} disabled={busy || !open} onClick={() => void close('1')}>
            {busy ? <Spinner /> : 'Close all'}
          </button>
          <button type="button" className={cx(btn, 'border border-line-strong')} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
}
